import type {
  ApprovalRequestId,
  ChatSkillReference,
  EnvironmentId,
  FollowUpDelivery,
  ModelSelection,
  OrchestrationThreadActivity,
  OrchestrationThreadGoal,
  ProjectEntry,
  ProviderApprovalDecision,
  ProviderInteractionMode,
  ProviderOptionSelection,
  ResolvedKeybindingsConfig,
  RuntimeMode,
  ScopedThreadRef,
  ServerProvider,
  ThreadId,
  TurnId,
} from "@threadlines/contracts";
import { type PromptRecall, recallablePrompts, stepPromptRecall } from "./composerPromptRecall";
import { containsWorkflowKeyword } from "@threadlines/shared/workflowRuns";
import { ComposerWorkflowKeywordHint } from "./ComposerWorkflowKeywordHint";
import {
  ProviderDriverKind,
  ProviderInstanceId,
  PROVIDER_SEND_TURN_MAX_ATTACHMENTS,
  PROVIDER_SEND_TURN_MAX_FILE_BYTES,
  PROVIDER_SEND_TURN_MAX_IMAGE_BYTES,
} from "@threadlines/contracts";
import { createModelSelection, normalizeModelSlug } from "@threadlines/shared/model";
import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { serializeComposerMentionPath } from "~/composerMentionPath";
import type { FileSelectionContextDraft } from "~/lib/fileSelectionContext";
import type { DrawingContext, DrawingContextDraft } from "~/lib/drawingContext";
import {
  pickedElementContextDedupKey,
  type PickedElementContext,
  type PickedElementContextDraft,
} from "~/lib/pickedElementContext";
import { projectSearchEntriesQueryOptions } from "~/lib/projectReactQuery";
import { providerSkillsQueryOptions } from "~/lib/providerSkillsReactQuery";
import { ComposerPendingFileSelectionContexts } from "./ComposerPendingFileSelectionContexts";
import {
  clampCollapsedComposerCursor,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  replaceTextRange,
} from "../../composer-logic";
import {
  deriveComposerSendState,
  desktopCapturedScreenshotToFile,
  readFileAsDataUrl,
} from "../ChatView.logic";
import {
  type ComposerAttachment,
  type DraftId,
  type PersistedComposerAttachment,
  composerAttachmentDedupKey,
  hydrateAttachmentsFromPersisted,
  useComposerDraftStore,
  useComposerThreadDraft,
  useEffectiveComposerModelState,
} from "../../composerDraftStore";
import {
  buildStashEntryContexts,
  MAX_STASH_ENTRIES,
  partitionStashAttachments,
  stashEntryContextDrafts,
  usePromptStashStore,
  type PromptStashEntry,
} from "../../promptStashStore";
import { compressImageForStash, readFileForStash } from "../../lib/stashImageCompression";
import { resolveShortcutCommand, shortcutLabelForCommand } from "../../keybindings";
import { isTerminalFocused } from "../../lib/terminalFocus";
import { useCommandPaletteStore } from "../../commandPaletteStore";
import {
  type TerminalContextDraft,
  type TerminalContextSelection,
  INLINE_TERMINAL_CONTEXT_PLACEHOLDER,
  removeInlineTerminalContextPlaceholder,
} from "../../lib/terminalContext";
import type {
  TranscriptHighlightContextDraft,
  TranscriptHighlightContextSelection,
} from "../../lib/transcriptHighlightContext";
import {
  COMPOSER_FOOTER_RESERVE_ATTRIBUTE,
  type ComposerFooterTier,
  INITIAL_COMPOSER_FOOTER_TIER_STATE,
  isComposerFooterOverflowing,
  measureComposerFooterOverflow,
  measureComposerFooterReserve,
  resetComposerFooterTierWidths,
  resolveComposerFooterTier,
  shouldUseCompactComposerPrimaryActions,
} from "../composerFooterLayout";
import {
  type ComposerCommandKey,
  type ComposerPromptEditorHandle,
  type ComposerSkillAvailability,
  ComposerPromptEditor,
} from "../ComposerPromptEditor";
import { type ComposerPromptSuggestionDisplay } from "./ComposerPromptSuggestion";
import { ProviderModelPicker } from "./ProviderModelPicker";
import { type ComposerCommandItem, ComposerCommandMenu } from "./ComposerCommandMenu";
import { useComposerMenuRoom } from "./useComposerMenuRoom";
import { ComposerPendingApprovalActions } from "./ComposerPendingApprovalActions";
import { CompactComposerControlsMenu } from "./CompactComposerControlsMenu";
import { ComposerAttachmentMenu } from "./ComposerAttachmentMenu";
import { ComposerStashControl } from "./ComposerStashControl";
import {
  type ComposerRoomDelivery,
  ComposerDeclineAndSendButton,
  ComposerPrimaryActions,
  ComposerRoomSendButton,
  ComposerStopButton,
  FOLLOW_UP_DELIVERY_LABELS,
  roomDeliveryLabels,
} from "./ComposerPrimaryActions";
import { useSettings, useUpdateSettings } from "../../hooks/useSettings";
import { useAgentWarmUp } from "../../hooks/useAgentWarmUp";
import { getThreadInFlightStatus } from "../../lib/threadSort";
import { ComposerAgentInvitePanel } from "./ComposerAgentInvitePanel";
import { ComposerChildThreadsPanel } from "./ComposerChildThreadsPanel";
import { ComposerScheduleRequestPanel } from "./ComposerScheduleRequestPanel";
import { useScheduledTaskRequestsForThread } from "../../lib/scheduledTasksState";
import { ComposerRemoteStartPanel } from "./ComposerRemoteStartPanel";
import { usePendingLinkRequests } from "../sidebar/linkedComputers";
import { pendingChildThreads } from "../../childThreads";
import { ComposerPendingApprovalPanel } from "./ComposerPendingApprovalPanel";
import { ComposerPendingUserInputPanel } from "./ComposerPendingUserInputPanel";
import {
  ComposerGoalBar,
  type ComposerGoalSetInput,
  type ThreadGoalControls,
} from "./ComposerGoalBar";
import { ComposerPlanFollowUpBanner } from "./ComposerPlanFollowUpBanner";
import { type ComposerNotice, selectComposerNotices } from "./composerNotices";
import { useModelRetirementNotice } from "./modelRetirementNotice";
import { ComposerDock, hasComposerDockContent } from "./ComposerDock";
import { NO_COMPOSER_PULL_REQUESTS, type ComposerPullRequest } from "./ComposerPullRequestRow";
import { ComposerPendingDrawingContexts } from "./ComposerPendingDrawingContexts";
import { ComposerPendingPickedElementContexts } from "./ComposerPendingPickedElementContexts";
import { ComposerPendingTranscriptHighlightContexts } from "./ComposerPendingTranscriptHighlightContexts";
import { ComposerPendingTerminalContexts } from "./ComposerPendingTerminalContexts";
import { resolveComposerMenuActiveItemId } from "./composerMenuHighlight";
import { isPluginSkillCommandName, searchSlashCommandItems } from "./composerSlashCommandSearch";
import {
  buildComposerCapabilityHint,
  buildDefaultComposerPlaceholder,
} from "./composerPlaceholder";
import {
  getComposerProviderState,
  renderProviderTraitsMenuContent,
  renderProviderTraitsPicker,
} from "./composerProviderState";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { isAppBackendEnvironment } from "../../environments/providerComputer";
import { ContextWindowMeter } from "./ContextWindowMeter";
import { ProviderUsageSignIn } from "./providerSignIn";
import { buildExpandedImagePreview, type ExpandedImagePreview } from "./ExpandedImagePreview";
import { usePinchToExpand } from "./usePinchToExpand";
import type { FilePreviewRequest } from "./FilePreviewDialog";
import { basenameOfPath } from "../../vscode-icons";
import { cn, randomUUID } from "~/lib/utils";
import { getInteractionModeToggleTitle, interactionModeConfig } from "../../interactionModeOptions";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { failureNotice, toastManager } from "../ui/toast";
import { useRoomAgents } from "./useRoomAgents";
import { scopedThreadKey } from "@threadlines/client-runtime";
import {
  buildRoomAgentLabels,
  canAnswerOnTheSide,
  hasRoomHistory,
  isRoom,
  isRoomWaitChosen,
  matchRoomAgents,
  ownAgentSession,
  pendingRoomInvite,
  resolveRoomDelivery,
  resolveRoomRecipient,
  resolveRoomSend,
  roomActivityAgent,
  roomAgentKey,
  roomTurnOwners,
  useRoomAgentOptions,
  useRoomRecipientStore,
} from "../../rooms";
import { getPickerModelName } from "./providerIconUtils";
import { awaitingInvite } from "@threadlines/shared/roomAgentRequests";
import { activeParticipants, hasAgentRecords } from "@threadlines/shared/threadParticipants";
import { shouldRenderTraitsControls, TraitsMenuContent, TraitsPicker } from "./TraitsPicker";
import {
  canRequestProviderRateLimitResetCredit,
  useProviderRateLimitResetCredit,
} from "../ProviderRateLimitResetCredit";
import { CircleAlertIcon, FileTextIcon, XIcon } from "lucide-react";
import { proposedPlanTitle } from "../../proposedPlan";
import {
  getProviderDisplayName,
  getProviderInteractionModeToggle,
  providerModelSupportsInputModality,
} from "../../providerModels";
import {
  deriveAccessLevel,
  runtimeModeConfig,
  type RuntimeModeOption,
} from "../../runtimeModeOptions";
import {
  deriveDisplayProviderInstanceEntries,
  resolveComposerProviderSelection,
  type ProviderInstanceEntry,
} from "../../providerInstances";
import { type AppModelOption, getAppModelOptionsForInstance } from "../../modelSelection";
import type { UnifiedSettings } from "@threadlines/contracts/settings";
import type { SessionPhase, Thread } from "../../types";
import type { PendingUserInputDraftAnswer } from "../../pendingUserInput";
import {
  deriveActiveModelFallbackState,
  isWaitingOnBackgroundTasks,
  type PendingApproval,
  type PendingUserInput,
} from "../../session-logic";
import { deriveLatestContextWindowSnapshot } from "../../lib/contextWindow";
import { resolvePromptSuggestionView, selectPromptSuggestion } from "../../lib/promptSuggestions";
import {
  deriveProviderAccountUsagePresentationForProvider,
  type ProviderAccountUsagePresentation,
} from "../../lib/providerUsage";
import { formatProviderSkillDisplayName } from "../../providerSkillPresentation";
import {
  FILE_ATTACHMENT_ACCEPT,
  resolveFileAttachmentType,
} from "@threadlines/shared/fileAttachments";
import { searchProviderSkills } from "../../providerSkillSearch";
import { resolveComposerSkillReferences } from "../../providerSkillReferences";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { ComposerDictationControl } from "./ComposerDictationControl";
import { useDictation } from "../../dictation/useDictation";

const ATTACHMENT_SIZE_LIMIT_LABEL = `${Math.round(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES / (1024 * 1024))}MB`;
const ALL_ATTACHMENT_ACCEPT = `image/*,${FILE_ATTACHMENT_ACCEPT}`;

const COMPOSER_PATH_QUERY_DEBOUNCE_MS = 120;
const CODEX_AGENT_PROVIDER = ProviderDriverKind.make("codex");
const CLAUDE_AGENT_PROVIDER = ProviderDriverKind.make("claudeAgent");
const EMPTY_PROJECT_ENTRIES: ProjectEntry[] = [];
const EMPTY_PROVIDER_SKILLS: ServerProvider["skills"] = [];
/** A thread with no agents besides its own, while there is no thread yet. */
const NO_ROOM_THREAD = {};
const COMPOSER_FLOATING_LAYER_SELECTOR = [
  '[data-slot="popover-popup"]',
  '[data-slot="menu-popup"]',
  '[data-slot="select-popup"]',
  '[data-slot="combobox-popup"]',
  '[data-slot="autocomplete-popup"]',
].join(",");

/** Menu rows for provider skills, shared by the `$` trigger and the `/` menu's
 *  Skills section. Both insert the same `$skill-name` token on select. */
function buildComposerSkillItems(
  skills: ServerProvider["skills"],
  provider: ProviderDriverKind,
): Array<Extract<ComposerCommandItem, { type: "skill" }>> {
  return skills.map((skill) => ({
    id: `skill:${provider}:${skill.name}`,
    type: "skill" as const,
    provider,
    skill,
    label: formatProviderSkillDisplayName(skill),
    description:
      skill.shortDescription ??
      skill.description ??
      (skill.scope ? `${skill.scope} skill` : "Run provider skill"),
  }));
}

const extendReplacementRangeForTrailingSpace = (
  text: string,
  rangeEnd: number,
  replacement: string,
): number => {
  if (!replacement.endsWith(" ")) {
    return rangeEnd;
  }
  return text[rangeEnd] === " " ? rangeEnd + 1 : rangeEnd;
};

const syncTerminalContextsByIds = (
  contexts: ReadonlyArray<TerminalContextDraft>,
  ids: ReadonlyArray<string>,
): TerminalContextDraft[] => {
  const contextsById = new Map(contexts.map((context) => [context.id, context]));
  return ids.flatMap((id) => {
    const context = contextsById.get(id);
    return context ? [context] : [];
  });
};

const terminalContextIdListsEqual = (
  contexts: ReadonlyArray<TerminalContextDraft>,
  ids: ReadonlyArray<string>,
): boolean =>
  contexts.length === ids.length && contexts.every((context, index) => context.id === ids[index]);

function isInsideComposerFloatingLayer(element: Element): boolean {
  return element.closest(COMPOSER_FLOATING_LAYER_SELECTOR) !== null;
}

function unknownErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function formatModelDisplayName(modelId: string, options: ReadonlyArray<AppModelOption>): string {
  const option = options.find((candidate) => candidate.slug === modelId);
  return option?.shortName ?? option?.name ?? modelId;
}

const ComposerFooterModeControls = memo(function ComposerFooterModeControls(props: {
  iconOnly: boolean;
  showInteractionModeToggle: boolean;
  interactionMode: ProviderInteractionMode;
  runtimeMode: RuntimeMode;
  runtimeModeOptions: ReadonlyArray<RuntimeModeOption>;
  onToggleInteractionMode: () => void;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
}) {
  const isPlanInteraction = props.interactionMode === "plan";
  const currentRuntimeModeOption = props.runtimeModeOptions.find(
    (option) => option.mode === props.runtimeMode,
  ) ?? {
    mode: props.runtimeMode,
    ...runtimeModeConfig[props.runtimeMode],
  };
  const RuntimeModeIcon = currentRuntimeModeOption.icon;
  const InteractionModeIcon = interactionModeConfig[props.interactionMode].icon;

  return (
    <>
      <Separator orientation="vertical" className="mx-0.5 hidden h-4 sm:block" />

      {props.showInteractionModeToggle ? (
        <>
          <Button
            variant="ghost"
            // Never shrinks: the footer watches this row for overflow to decide
            // when every label drops to an icon, and a truncating label would
            // hide the very overflow it is looking for.
            className={cn(
              "shrink-0 whitespace-nowrap text-muted-foreground/70 hover:text-foreground/80",
              !props.iconOnly && "px-2 sm:px-3",
            )}
            size={props.iconOnly ? "icon-sm" : "sm"}
            type="button"
            aria-label={`Interaction mode: ${interactionModeConfig[props.interactionMode].label}`}
            onClick={props.onToggleInteractionMode}
            tooltip={getInteractionModeToggleTitle(props.interactionMode)}
          >
            <InteractionModeIcon
              className={cn("size-3.5", isPlanInteraction && "text-primary-readable opacity-100")}
            />
            <span className={cn("sr-only", !props.iconOnly && "sm:not-sr-only")}>
              {interactionModeConfig[props.interactionMode].label}
            </span>
          </Button>

          <Separator orientation="vertical" className="mx-0.5 hidden h-4 sm:block" />
        </>
      ) : null}

      <Select
        value={props.runtimeMode}
        onValueChange={(value) => props.onRuntimeModeChange(value!)}
      >
        <SelectTrigger
          variant="ghost"
          size="sm"
          className={cn(
            "shrink-0 font-medium",
            props.iconOnly &&
              "min-h-7 w-7 justify-center gap-0 px-0 [&_[data-slot=select-icon]]:hidden",
            isPlanInteraction && "opacity-65",
          )}
          aria-label={`Access level: ${currentRuntimeModeOption.label}`}
          title={
            isPlanInteraction
              ? "Plan turns don't edit files. This access level applies when you build."
              : currentRuntimeModeOption.description
          }
        >
          <RuntimeModeIcon className="size-3.5" />
          {!props.iconOnly ? <SelectValue>{currentRuntimeModeOption.label}</SelectValue> : null}
        </SelectTrigger>
        <SelectPopup alignItemWithTrigger={false}>
          {props.runtimeModeOptions.map((option) => {
            const OptionIcon = option.icon;
            return (
              <SelectItem
                key={option.mode}
                value={option.mode}
                disabled={option.disabled === true}
                className="min-w-64 py-2"
              >
                <div className="grid min-w-0 gap-0.5">
                  <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                    <OptionIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    {option.label}
                  </span>
                  <span className="text-muted-foreground text-xs leading-4">
                    {option.disabled && option.disabledReason
                      ? option.disabledReason
                      : option.description}
                  </span>
                </div>
              </SelectItem>
            );
          })}
        </SelectPopup>
      </Select>
    </>
  );
});

const ComposerFooterPrimaryActions = memo(function ComposerFooterPrimaryActions(props: {
  compact: boolean;
  activeContextWindow: ReturnType<typeof deriveLatestContextWindowSnapshot>;
  providerAccountUsage: ProviderAccountUsagePresentation | null;
  providerUsageInstanceId: ProviderInstanceId | null;
  contextWindowLabel: string | null;
  isPreparingWorktree: boolean;
  isRunning: boolean;
  showPlanFollowUpPrompt: boolean;
  promptHasText: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  hasSendableContent: boolean;
  preserveComposerFocusOnPointerDown?: boolean;
  runtimeMode: RuntimeMode;
  runtimeModeOptions: ReadonlyArray<RuntimeModeOption>;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
  followUpDelivery: FollowUpDelivery;
  onFollowUpDeliveryChange: (delivery: FollowUpDelivery) => void;
  roomDelivery: ComposerRoomDelivery | null;
  onRoomDeliveryChange: (delivery: FollowUpDelivery) => void;
  onStopBackgroundWork: (() => void) | undefined;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
  onResetAccountUsage?: (() => void) | undefined;
  accountUsageResetInFlight?: boolean | undefined;
  onCompactContext?: (() => void) | undefined;
  contextCompactDisabled?: boolean | undefined;
  contextCompactInFlight?: boolean | undefined;
  contextCompactDisabledReason?: string | null | undefined;
}) {
  return (
    <>
      <ContextWindowMeter
        usage={props.activeContextWindow}
        accountUsage={props.providerAccountUsage}
        accountUsageSignIn={
          props.providerAccountUsage?.notice?.signIn && props.providerUsageInstanceId ? (
            <ProviderUsageSignIn
              instanceId={props.providerUsageInstanceId}
              className="h-5 shrink-0 px-1.5 text-[10px] sm:h-5 sm:text-[10px]"
            />
          ) : undefined
        }
        contextWindowLabel={props.contextWindowLabel}
        onResetAccountUsage={props.onResetAccountUsage}
        accountUsageResetInFlight={props.accountUsageResetInFlight}
        onCompactContext={props.onCompactContext}
        contextCompactDisabled={props.contextCompactDisabled}
        contextCompactInFlight={props.contextCompactInFlight}
        contextCompactDisabledReason={props.contextCompactDisabledReason}
      />
      <ComposerPrimaryActions
        compact={props.compact}
        isRunning={props.isRunning}
        showPlanFollowUpPrompt={props.showPlanFollowUpPrompt}
        promptHasText={props.promptHasText}
        isSendBusy={props.isSendBusy}
        isConnecting={props.isConnecting}
        isEnvironmentUnavailable={props.isEnvironmentUnavailable}
        isPreparingWorktree={props.isPreparingWorktree}
        hasSendableContent={props.hasSendableContent}
        preserveComposerFocusOnPointerDown={props.preserveComposerFocusOnPointerDown ?? false}
        runtimeMode={props.runtimeMode}
        runtimeModeOptions={props.runtimeModeOptions}
        onRuntimeModeChange={props.onRuntimeModeChange}
        followUpDelivery={props.followUpDelivery}
        onFollowUpDeliveryChange={props.onFollowUpDeliveryChange}
        roomDelivery={props.roomDelivery}
        onRoomDeliveryChange={props.onRoomDeliveryChange}
        onStopBackgroundWork={props.onStopBackgroundWork}
        onInterrupt={props.onInterrupt}
        onImplementPlanInNewThread={props.onImplementPlanInNewThread}
      />
    </>
  );
});

// --------------------------------------------------------------------------
// Handle exposed to ChatView
// --------------------------------------------------------------------------

export interface ChatComposerHandle {
  focusAtEnd: () => void;
  focusAt: (cursor: number) => void;
  openModelPicker: () => void;
  openGoalEditor: () => void;
  toggleModelPicker: () => void;
  isModelPickerOpen: () => boolean;
  readSnapshot: () => {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  };
  /** Reset composer cursor/trigger/highlight after external prompt mutations (e.g. onSend). */
  resetCursorState: (options?: {
    cursor?: number;
    prompt?: string;
    detectTrigger?: boolean;
  }) => void;
  /** Insert a terminal context from the terminal drawer. */
  addTerminalContext: (selection: TerminalContextSelection) => void;
  /** Add a note attached to selected transcript text. */
  addTranscriptHighlightContext: (selection: TranscriptHighlightContextSelection) => void;
  /** Attach an element picked in the browser preview. */
  addPickedElementContext: (context: PickedElementContext) => void;
  /** Attach a screenshot captured from the browser preview. Returns whether
   *  it actually attached, so the capture surface can confirm honestly. */
  addScreenshotAttachment: (input: { dataUrl: string; name: string }) => boolean;
  /** Attach a small generated text file (the page's reported errors, say).
   *  Returns whether it actually attached. */
  addTextFileAttachment: (input: { name: string; text: string }) => boolean;
  /** Attach a drawing made on the page: one chip carrying the picture, the
   *  note and whatever the closed strokes went round. */
  addDrawingContext: (context: DrawingContext) => void;
  /** Get the current prompt/effort/model state for use in send. */
  getSendContext: () => {
    prompt: string;
    images: ComposerAttachment[];
    terminalContexts: TerminalContextDraft[];
    transcriptHighlightContexts: TranscriptHighlightContextDraft[];
    fileSelectionContexts: FileSelectionContextDraft[];
    pickedElementContexts: PickedElementContextDraft[];
    drawingContexts: DrawingContextDraft[];
    selectedPromptEffort: string | null;
    selectedModelOptionsForDispatch: unknown;
    selectedModelSelection: ModelSelection;
    selectedProvider: ProviderDriverKind;
    selectedModel: string;
    selectedProviderModels: ReadonlyArray<ServerProvider["models"][number]>;
    skillReferences: ChatSkillReference[];
  };
}

// --------------------------------------------------------------------------
// Props
// --------------------------------------------------------------------------

export interface ChatComposerProps {
  composerDraftTarget: ScopedThreadRef | DraftId;
  environmentId: EnvironmentId;
  routeKind: "server" | "draft";
  routeThreadRef: ScopedThreadRef;
  draftId: DraftId | null;

  // Thread context
  activeThreadId: ThreadId | null;
  activeThreadEnvironmentId: EnvironmentId | undefined;
  activeThread: Thread | undefined;
  isLocalDraftThread: boolean;
  /** The thread exists on the server, even while its route is still the draft's. */
  serverThreadExists: boolean;

  // Session phase
  phase: SessionPhase;
  isConnecting: boolean;
  isSendBusy: boolean;
  isPreparingWorktree: boolean;
  environmentUnavailable: {
    readonly label: string;
    readonly connectionState: "connecting" | "disconnected" | "error";
  } | null;
  /**
   * The thread takes no messages here: it is moving, or moved, to another
   * computer (docs/design/fleet.md, Part 3). `reason` is the placeholder.
   */
  threadLocked?: { readonly reason: string } | null;

  // Pending approvals / inputs
  activePendingApproval: PendingApproval | null;
  pendingApprovals: PendingApproval[];
  pendingUserInputs: PendingUserInput[];
  activePendingDraftAnswers: Record<string, PendingUserInputDraftAnswer>;
  activePendingQuestionIndex: number;
  respondingRequestIds: ApprovalRequestId[];
  respondingUserInputRequestIds: ApprovalRequestId[];
  /** True while the timeline is scrolled away from the bottom; auto-collapses the questions panel. */
  isTimelineScrolledAway: boolean;

  // Plan
  showPlanFollowUpPrompt: boolean;
  activeProposedPlan: Thread["proposedPlans"][number] | null;

  // Mode
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;

  // Provider / model
  lockedProvider: ProviderDriverKind | null;
  providerStatuses: ServerProvider[];
  activeThreadModelSelection: ModelSelection | null | undefined;

  // Context window
  activeThreadActivities: Thread["activities"] | undefined;

  /**
   * Active send-blocking notices, worst first. The dock renders the first one
   * attached to the composer's top edge, so a non-empty list also squares the
   * composer's top corners.
   */
  notices: ReadonlyArray<ComposerNotice>;

  /**
   * The thread's pull requests, docked above the notices: the one on its own
   * branch first, then the ones its agent opened elsewhere. Empty where the
   * thread has none, or where the surface has no route to resolve them (a
   * draft, a general chat).
   */
  pullRequests: ReadonlyArray<ComposerPullRequest>;

  /**
   * Lines that sit on top of the message box, such as queued messages. They
   * render inside the composer form, above the input.
   */
  stackedAbove?: ReactNode;

  // Misc
  resolvedTheme: "light" | "dark";
  settings: UnifiedSettings;
  /** Rooms for the computer this thread lives on (useRoomsEnabled). */
  roomsEnabled: boolean;
  keybindings: ResolvedKeybindingsConfig;
  terminalOpen: boolean;
  gitCwd: string | null;
  canReferenceWorkspaceFiles: boolean;

  // Refs the parent needs kept in sync
  promptRef: React.RefObject<string>;
  composerAttachmentsRef: React.RefObject<ComposerAttachment[]>;
  composerTerminalContextsRef: React.RefObject<TerminalContextDraft[]>;
  composerTranscriptHighlightContextsRef: React.RefObject<TranscriptHighlightContextDraft[]>;
  composerFileSelectionContextsRef: React.RefObject<FileSelectionContextDraft[]>;
  composerPickedElementContextsRef: React.RefObject<PickedElementContextDraft[]>;
  /** Shows a picked element again in the preview; absent outside the desktop app. */
  onRevealPickedElement?: ((context: PickedElementContextDraft) => void) | undefined;
  composerRef: React.RefObject<ChatComposerHandle | null>;

  // Scroll
  shouldAutoScrollRef: React.RefObject<boolean>;
  scheduleStickToBottom: () => void;

  // Callbacks
  onSend: (e?: { preventDefault: () => void }) => void;
  onInterrupt: () => void;
  /** The user chose to send a message holding "ultracode" as plain text. */
  workflowKeywordSuppressed?: boolean | undefined;
  onToggleWorkflowKeyword?: (() => void) | undefined;
  /** Ends the background work a settled turn left the thread waiting on. */
  onStopBackgroundWork: () => void;
  // Goal (Codex and Claude goal mode)
  /** What goals offer on the agent a goal would go to; null when it has none. */
  goalControls?: ThreadGoalControls | null | undefined;
  goalDispatching?: boolean | undefined;
  /** Freshly dispatched goal state not yet confirmed by the projection —
   *  preferred over the (possibly stale) server goal while present. */
  optimisticGoal?: OrchestrationThreadGoal | null | undefined;
  onSetThreadGoal?: ((input: ComposerGoalSetInput) => void) | undefined;
  onClearThreadGoal?: (() => void) | undefined;
  onCompactContext?: (() => void) | undefined;
  contextCompactDisabled?: boolean | undefined;
  contextCompactInFlight?: boolean | undefined;
  contextCompactDisabledReason?: string | null | undefined;
  onImplementPlanInNewThread: () => void;
  onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<void>;
  onSelectActivePendingUserInputOption: (questionId: string, optionLabel: string) => void;
  onAdvanceActivePendingUserInput: () => void;
  onRespondToUserInput?: (requestId: ApprovalRequestId, answers: Record<string, unknown>) => void;
  onPreviousActivePendingUserInputQuestion: () => void;
  onChangeActivePendingUserInputCustomAnswer: (questionId: string, value: string) => void;

  onProviderModelSelect: (instanceId: ProviderInstanceId, model: string) => void;
  toggleInteractionMode: () => void;
  handleRuntimeModeChange: (mode: RuntimeMode) => void;
  handleInteractionModeChange: (mode: ProviderInteractionMode) => void;

  focusComposer: () => void;
  setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  onExpandImage: (preview: ExpandedImagePreview) => void;
  onPreviewFile: (request: FilePreviewRequest) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

/** A picture attached to the draft; clicking or pinching it opens the viewer. */
function ComposerImageAttachmentButton(props: {
  readonly name: string;
  readonly previewUrl: string;
  readonly onExpand: () => void;
}) {
  const pinchRef = usePinchToExpand(props.onExpand);
  return (
    <button
      ref={pinchRef}
      type="button"
      className="h-full w-full cursor-zoom-in"
      aria-label={`Preview ${props.name}`}
      onClick={props.onExpand}
    >
      <img src={props.previewUrl} alt={props.name} className="h-full w-full object-cover" />
    </button>
  );
}

export const ChatComposer = memo(function ChatComposer(props: ChatComposerProps) {
  const {
    composerDraftTarget,
    environmentId,
    routeKind,
    routeThreadRef,
    draftId,
    activeThreadId,
    activeThreadEnvironmentId: _activeThreadEnvironmentId,
    activeThread,
    isLocalDraftThread,
    serverThreadExists,
    phase,
    isConnecting,
    isSendBusy,
    isPreparingWorktree,
    environmentUnavailable,
    threadLocked = null,
    activePendingApproval,
    pendingApprovals,
    pendingUserInputs,
    activePendingDraftAnswers,
    activePendingQuestionIndex,
    respondingRequestIds,
    respondingUserInputRequestIds,
    isTimelineScrolledAway,
    showPlanFollowUpPrompt,
    activeProposedPlan,
    runtimeMode,
    interactionMode,
    lockedProvider,
    providerStatuses,
    activeThreadModelSelection,
    activeThreadActivities,
    notices,
    pullRequests,
    stackedAbove,
    resolvedTheme,
    settings,
    roomsEnabled,
    keybindings,
    terminalOpen,
    gitCwd,
    canReferenceWorkspaceFiles,
    promptRef,
    composerRef,
    composerAttachmentsRef,
    composerTerminalContextsRef,
    composerTranscriptHighlightContextsRef,
    composerFileSelectionContextsRef,
    composerPickedElementContextsRef,
    onRevealPickedElement,
    shouldAutoScrollRef,
    scheduleStickToBottom,
    onSend,
    workflowKeywordSuppressed,
    onToggleWorkflowKeyword,
    onInterrupt,
    onStopBackgroundWork,
    goalControls,
    goalDispatching,
    optimisticGoal,
    onSetThreadGoal,
    onClearThreadGoal,
    onCompactContext,
    contextCompactDisabled,
    contextCompactInFlight,
    contextCompactDisabledReason,
    onImplementPlanInNewThread,
    onRespondToApproval,
    onSelectActivePendingUserInputOption,
    onAdvanceActivePendingUserInput,
    onRespondToUserInput,
    onPreviousActivePendingUserInputQuestion,
    onChangeActivePendingUserInputCustomAnswer,
    onProviderModelSelect,
    toggleInteractionMode,
    handleRuntimeModeChange,
    handleInteractionModeChange,
    focusComposer,
    setThreadError,
    onExpandImage,
    onPreviewFile,
  } = props;

  // ------------------------------------------------------------------
  // Store subscriptions (prompt / images / terminal contexts)
  // ------------------------------------------------------------------
  const composerDraft = useComposerThreadDraft(composerDraftTarget);
  const prompt = composerDraft.prompt;
  const composerAttachments = composerDraft.attachments;
  const composerTerminalContexts = composerDraft.terminalContexts;
  const composerTranscriptHighlightContexts = composerDraft.transcriptHighlightContexts;
  const composerFileSelectionContexts = composerDraft.fileSelectionContexts;
  const composerPickedElementContexts = composerDraft.pickedElementContexts;
  const composerDrawingContexts = composerDraft.drawingContexts;
  const nonPersistedComposerImageIds = composerDraft.nonPersistedAttachmentIds;

  const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
  const addComposerDraftAttachment = useComposerDraftStore((store) => store.addAttachment);
  const addComposerDraftAttachments = useComposerDraftStore((store) => store.addAttachments);
  const removeComposerDraftAttachment = useComposerDraftStore((store) => store.removeAttachment);
  const addComposerDraftTerminalContext = useComposerDraftStore(
    (store) => store.addTerminalContext,
  );
  const removeComposerDraftTerminalContext = useComposerDraftStore(
    (store) => store.removeTerminalContext,
  );
  const setComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.setTerminalContexts,
  );
  const removeComposerDraftFileSelectionContext = useComposerDraftStore(
    (store) => store.removeFileSelectionContext,
  );
  const addComposerDraftTranscriptHighlightContext = useComposerDraftStore(
    (store) => store.addTranscriptHighlightContext,
  );
  const setComposerDraftPickedElementContexts = useComposerDraftStore(
    (store) => store.setPickedElementContexts,
  );
  /**
   * Writes the list to the store and through to the ref that reads it back.
   *
   * The ref is refreshed by an effect, so it still holds the pre-call value
   * until the next render. Two of these in one tick would each build on that
   * same stale list and the second would overwrite the first -- which is
   * exactly what a region pick does, arriving as several elements at once.
   */
  const commitPickedElementContexts = useCallback(
    (next: PickedElementContextDraft[]) => {
      composerPickedElementContextsRef.current = next;
      setComposerDraftPickedElementContexts(composerDraftTarget, next);
    },
    [composerDraftTarget, composerPickedElementContextsRef, setComposerDraftPickedElementContexts],
  );
  const setComposerDraftDrawingContexts = useComposerDraftStore(
    (store) => store.setDrawingContexts,
  );
  const drawingContextsRef = useRef(composerDrawingContexts);
  drawingContextsRef.current = composerDrawingContexts;
  const commitDrawingContexts = useCallback(
    (next: DrawingContextDraft[]) => {
      drawingContextsRef.current = next;
      setComposerDraftDrawingContexts(composerDraftTarget, next);
    },
    [composerDraftTarget, setComposerDraftDrawingContexts],
  );
  const updateDrawingNote = useCallback(
    (contextId: string, note: string) => {
      commitDrawingContexts(
        drawingContextsRef.current.map((context) =>
          context.id === contextId ? { ...context, note: note === "" ? null : note } : context,
        ),
      );
    },
    [commitDrawingContexts],
  );
  const removeDrawingContext = useCallback(
    (contextId: string) => {
      commitDrawingContexts(
        drawingContextsRef.current.filter((context) => context.id !== contextId),
      );
    },
    [commitDrawingContexts],
  );

  const updatePickedElementNote = useCallback(
    (contextId: string, note: string) => {
      commitPickedElementContexts(
        composerPickedElementContextsRef.current.map((context) =>
          context.id === contextId ? { ...context, note: note === "" ? null : note } : context,
        ),
      );
    },
    [commitPickedElementContexts, composerPickedElementContextsRef],
  );
  const removePickedElementContext = useCallback(
    (contextId: string) => {
      commitPickedElementContexts(
        composerPickedElementContextsRef.current.filter((context) => context.id !== contextId),
      );
    },
    [commitPickedElementContexts, composerPickedElementContextsRef],
  );
  const removeComposerDraftTranscriptHighlightContext = useComposerDraftStore(
    (store) => store.removeTranscriptHighlightContext,
  );
  const updateComposerDraftTranscriptHighlightContextNote = useComposerDraftStore(
    (store) => store.updateTranscriptHighlightContextNote,
  );
  const clearComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.clearPersistedAttachments,
  );
  const syncComposerDraftPersistedAttachments = useComposerDraftStore(
    (store) => store.syncPersistedAttachments,
  );
  const getComposerDraft = useComposerDraftStore((store) => store.getComposerDraft);
  const clearStashableComposerContent = useComposerDraftStore(
    (store) => store.clearStashableComposerContent,
  );
  const addComposerDraftTerminalContexts = useComposerDraftStore(
    (store) => store.addTerminalContexts,
  );
  const addComposerDraftTranscriptHighlightContexts = useComposerDraftStore(
    (store) => store.addTranscriptHighlightContexts,
  );
  const addComposerDraftFileSelectionContext = useComposerDraftStore(
    (store) => store.addFileSelectionContext,
  );

  // ------------------------------------------------------------------
  // Model state
  // ------------------------------------------------------------------
  // Instance-aware projection of the wire provider list. One entry per
  // configured instance (default built-in + any custom `providerInstances.*`),
  // sorted default-first per driver kind for a stable picker order.
  const providerInstanceEntries = useMemo<ReadonlyArray<ProviderInstanceEntry>>(
    () => deriveDisplayProviderInstanceEntries(providerStatuses),
    [providerStatuses],
  );
  // The model controls belong to the thread's own agent; in a room the session
  // slot may hold another agent's runtime.
  const ownSession = ownAgentSession(activeThread);
  // The instance the composer targets and its agent, from one rule: the
  // user's unsaved pick, then the session's instance, then the thread's
  // saved one, each only while enabled (resolveComposerProviderSelection).
  const providerSelection = useMemo(
    () =>
      resolveComposerProviderSelection({
        entries: providerInstanceEntries,
        candidates: [
          composerDraft.activeProvider,
          ownSession?.providerInstanceId,
          activeThreadModelSelection?.instanceId,
        ],
        lockedProvider,
        fallbackDriverKind: ownSession?.provider ?? ProviderDriverKind.make("codex"),
      }),
    [
      activeThreadModelSelection?.instanceId,
      composerDraft.activeProvider,
      lockedProvider,
      ownSession?.provider,
      ownSession?.providerInstanceId,
      providerInstanceEntries,
    ],
  );
  const selectedProvider: ProviderDriverKind = lockedProvider ?? providerSelection.driverKind;
  // Claude runs a message holding "ultracode" as a workflow; the composer says
  // so as it is typed (and offers to send it as plain text instead).
  const workflowKeywordActive =
    selectedProvider === CLAUDE_AGENT_PROVIDER && containsWorkflowKeyword(prompt);
  const threadGoal = optimisticGoal ?? activeThread?.goal ?? null;
  const goalSupported =
    goalControls != null &&
    activeThreadId !== null &&
    onSetThreadGoal !== undefined &&
    onClearThreadGoal !== undefined;

  const selectedInstanceId = providerSelection.instanceId;

  const { modelOptions: composerModelOptions, selectedModel } = useEffectiveComposerModelState({
    threadRef: composerDraftTarget,
    providers: providerStatuses,
    selectedProvider,
    selectedInstanceId,
    threadModelSelection: activeThreadModelSelection,
    settings,
  });

  // Resolve the active instance's snapshot by `instanceId` so a custom
  // instance gets its own slash commands, skills, and model list — not
  // the first snapshot for the same driver kind.
  const selectedProviderEntry = useMemo(
    () => providerInstanceEntries.find((entry) => entry.instanceId === selectedInstanceId),
    [providerInstanceEntries, selectedInstanceId],
  );
  const selectedProviderStatus = useMemo(
    () => selectedProviderEntry?.snapshot ?? null,
    [selectedProviderEntry],
  );
  // Heading for the provider section of the `/` menu — the instance's own name
  // when it has one, so custom instances read as themselves.
  const selectedProviderDisplayName = useMemo(
    () =>
      selectedProviderStatus?.displayName?.trim() ||
      getProviderDisplayName(providerStatuses, selectedProvider),
    [providerStatuses, selectedProvider, selectedProviderStatus],
  );
  const projectSkillsQuery = useQuery(
    providerSkillsQueryOptions({
      environmentId,
      cwd: gitCwd,
      providerInstanceId: selectedInstanceId,
      enabled: selectedProvider === CODEX_AGENT_PROVIDER,
    }),
  );
  const composerSkills = projectSkillsQuery.data ?? EMPTY_PROVIDER_SKILLS;
  // `$skill` is Codex-native, so a draft carried to another provider sends its
  // chips as literal words. Chips read this to warn; the list only counts as
  // authoritative once Codex's skills have actually loaded.
  const composerSkillAvailability = useMemo<ComposerSkillAvailability>(
    () => ({
      knownSkillNames: new Set(
        composerSkills.filter((skill) => skill.enabled).map((skill) => skill.name),
      ),
      authoritative:
        selectedProvider !== CODEX_AGENT_PROVIDER || projectSkillsQuery.data !== undefined,
      staleReason:
        selectedProvider === CODEX_AGENT_PROVIDER
          ? "Skill not found in this workspace"
          : "Not available with the selected provider",
    }),
    [composerSkills, projectSkillsQuery.data, selectedProvider],
  );
  const {
    isConsumingRateLimitResetCredit,
    requestRateLimitResetCredit,
    rateLimitResetCreditDialog,
  } = useProviderRateLimitResetCredit();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const selectedProviderModels = useMemo<ReadonlyArray<ServerProvider["models"][number]>>(
    () => selectedProviderEntry?.models ?? [],
    [selectedProviderEntry],
  );
  const selectedModelSupportsImages = useMemo(
    () =>
      providerModelSupportsInputModality(
        selectedProviderModels,
        selectedModel,
        selectedProvider,
        "image",
      ),
    [selectedProvider, selectedProviderModels, selectedModel],
  );

  const composerProviderState = useMemo(
    () =>
      getComposerProviderState({
        provider: selectedProvider,
        model: selectedModel,
        models: selectedProviderModels,
        prompt,
        modelOptions: composerModelOptions?.[selectedInstanceId],
      }),
    [
      composerModelOptions,
      prompt,
      selectedInstanceId,
      selectedModel,
      selectedProvider,
      selectedProviderModels,
    ],
  );

  const selectedPromptEffort = composerProviderState.promptEffort;
  const selectedModelOptionsForDispatch = composerProviderState.modelOptionsForDispatch;
  const composerProviderControls = useMemo(
    () => ({
      showInteractionModeToggle: getProviderInteractionModeToggle(
        providerStatuses,
        selectedProvider,
      ),
    }),
    [providerStatuses, selectedProvider],
  );
  const selectedModelSelection = useMemo<ModelSelection>(
    () => createModelSelection(selectedInstanceId, selectedModel, selectedModelOptionsForDispatch),
    [selectedInstanceId, selectedModel, selectedModelOptionsForDispatch],
  );
  const selectedModelForPicker = selectedModel;
  // Instance-keyed option list so the picker can show each configured
  // instance (built-in + custom) as a first-class sidebar entry. The
  // options are server-reported models plus that exact instance's
  // configured custom models; selected slugs are not injected into lists.
  const modelOptionsByInstance = useMemo<
    ReadonlyMap<ProviderInstanceId, ReadonlyArray<AppModelOption>>
  >(() => {
    const out = new Map<ProviderInstanceId, ReadonlyArray<AppModelOption>>();
    for (const entry of providerInstanceEntries) {
      out.set(entry.instanceId, getAppModelOptionsForInstance(settings, entry));
    }
    return out;
  }, [providerInstanceEntries, settings]);
  const selectedModelForPickerWithCustomFallback = useMemo(() => {
    const currentOptions = modelOptionsByInstance.get(selectedInstanceId) ?? [];
    return currentOptions.some((option) => option.slug === selectedModelForPicker)
      ? selectedModelForPicker
      : (normalizeModelSlug(selectedModelForPicker, selectedProvider) ?? selectedModelForPicker);
  }, [modelOptionsByInstance, selectedInstanceId, selectedModelForPicker, selectedProvider]);
  const activeModelFallback = useMemo(
    () => deriveActiveModelFallbackState(activeThreadActivities ?? [], activeThread?.latestTurn),
    [activeThread?.latestTurn, activeThreadActivities],
  );

  // Rooms: which agent the next message goes to, picked in the model picker.
  // A new thread can take agents too; its first message brings them along.
  const roomsShown = roomsEnabled && activeThread !== undefined;
  const chosenRoomRecipientId = useRoomRecipientStore(
    (state) => state.chosen[scopedThreadKey(routeThreadRef)],
  );
  const roomRecipientId =
    activeThread !== undefined ? resolveRoomRecipient(activeThread, chosenRoomRecipientId) : null;
  // Start the agent this message is for while it's being written, so it's
  // ready when sent (docs/early-agent-start.md). Picked the way sending
  // picks it, Rooms switch included.
  const warmUpRoute =
    activeThread !== undefined
      ? resolveRoomSend({ enabled: roomsEnabled, thread: activeThread, threadRef: routeThreadRef })
      : null;
  useAgentWarmUp(
    environmentId,
    {
      enabled: settings.startAgentsWhileTyping !== false,
      threadId: activeThread?.id ?? null,
      serverThreadExists,
      // Background work counts: a message to another agent then goes on the
      // side, not to that agent's own session.
      turnActive:
        phase === "running" ||
        (activeThread !== undefined && getThreadInFlightStatus(activeThread) !== null) ||
        isWaitingOnBackgroundTasks(activeThread?.latestTurn ?? null, activeThread?.session ?? null),
      participantId: warmUpRoute?.recipient?.id ?? null,
      modelSelection: warmUpRoute?.modelSelection ?? selectedModelSelection,
      runtimeMode,
    },
    prompt,
  );
  // "Send when done" picked for the message being written; asking now is the
  // default for each new one.
  const roomTurnId = activeThread?.latestTurn?.turnId;
  const roomWaitChosen = useRoomRecipientStore((state) =>
    isRoomWaitChosen(state.waitChosen, routeThreadRef, roomTurnId),
  );
  const handleRoomDeliveryChange = useCallback(
    (delivery: FollowUpDelivery) =>
      useRoomRecipientStore.getState().chooseWait(routeThreadRef, roomTurnId, delivery === "queue"),
    [routeThreadRef, roomTurnId],
  );
  // An added agent keeps the model and reasoning it joined with; the model and
  // reasoning controls belong to the thread's own agent.
  const addressingRoomAgent = roomsShown && roomRecipientId !== null;
  const roomWorkingId =
    activeThread?.session?.orchestrationStatus === "running" ||
    activeThread?.session?.orchestrationStatus === "starting"
      ? (activeThread.session.participantId ?? null)
      : undefined;
  // Every agent in a room by the name the model picker shows; null outside
  // rooms. The thread's own agent is named by the model its next message will
  // use: the one picked here, which the thread takes on when it is sent.
  const roomAgentLabels = useMemo(
    () =>
      roomsShown && activeThread && hasRoomHistory(activeThread)
        ? buildRoomAgentLabels(
            {
              modelSelection: selectedModelSelection,
              participants: activeThread.participants,
              agentRole: activeThread.agentRole,
            },
            providerInstanceEntries,
            (model, entry) => getPickerModelName(model, entry.driverKind),
          )
        : null,
    [
      selectedModelSelection,
      activeThread?.participants,
      activeThread?.agentRole,
      providerInstanceEntries,
      roomsShown,
    ],
  );
  const roomAgents = useRoomAgents({
    threadRef: routeThreadRef,
    // Until the thread exists on the server, its agents live in its draft;
    // while its first message is on its way, they wait.
    draftTarget: isLocalDraftThread && !serverThreadExists ? composerDraftTarget : null,
    frozen: isLocalDraftThread && isSendBusy,
    thread: activeThread ?? NO_ROOM_THREAD,
    primaryModelSelection: selectedModelSelection,
    instanceEntries: providerInstanceEntries,
    workingId: roomWorkingId,
    answeringId: activeThread?.sideTurn?.participantId,
  });
  // An agent's invite waiting for the user, with every agent it names. Shown
  // whether or not this device shows rooms: the thread waits on it either way.
  const pendingInvite = useMemo(
    () =>
      activeThread?.agentRequests && awaitingInvite(activeThread.agentRequests) !== undefined
        ? pendingRoomInvite(
            activeThread,
            buildRoomAgentLabels(
              {
                modelSelection: activeThread.modelSelection,
                participants: activeThread.participants,
                agentRole: activeThread.agentRole,
              },
              providerInstanceEntries,
              (model, entry) => getPickerModelName(model, entry.driverKind),
            ),
          )
        : null,
    [activeThread, providerInstanceEntries],
  );
  // Threads the agent asked to start, waiting for the user's yes (child
  // threads, "Ask me first"). The thread waits on it like on an invite.
  const pendingChildThreadStarts = useMemo(
    () =>
      activeThread
        ? pendingChildThreads(activeThread, providerInstanceEntries, (model, entry) =>
            getPickerModelName(model, entry.driverKind),
          )
        : null,
    [activeThread, providerInstanceEntries],
  );
  // A change to the scheduled tasks the agent asked for, waiting for the
  // user's yes ("Ask me first"): the child-thread card's schedule kind.
  const pendingScheduleRequest = useScheduledTaskRequestsForThread(
    routeThreadRef.environmentId,
    routeThreadRef.threadId,
  )[0];
  // A thread the agent asked another computer to start, waiting for that
  // computer's user: the card that decides it sits here, in the thread that
  // asked, where the conversation says why.
  const pendingLinkRequests = usePendingLinkRequests();
  const pendingRemoteStart = useMemo(
    () =>
      activeThread
        ? (pendingLinkRequests.find(
            ({ request }) =>
              request.parentEnvironmentId === activeThread.environmentId &&
              request.parentThreadId === activeThread.id,
          ) ?? null)
        : null,
    [activeThread, pendingLinkRequests],
  );
  // The agents "@" offers in a room, first in the menu above files. Picking
  // one sends the message to it, like the agent picker.
  const roomMentionAgents = useMemo(() => {
    // Only while another agent is in the thread: with all of them gone, "@"
    // is for files again.
    if (roomAgentLabels === null || activeThread === undefined || !isRoom(activeThread)) {
      return [];
    }
    const answeringId = activeThread.sideTurn?.participantId;
    return [
      null,
      ...activeParticipants({ participants: activeThread.participants ?? [] }).map((p) => p.id),
    ].map((participantId) => {
      const label = roomAgentLabels.get(roomAgentKey(participantId));
      return {
        participantId,
        name: label?.name ?? "Agent",
        entry: label?.entry,
        status:
          roomWorkingId !== undefined && roomWorkingId === participantId
            ? "working"
            : answeringId !== undefined && answeringId === participantId
              ? "answering"
              : participantId === null
                ? "main"
                : "",
      };
    });
  }, [activeThread, roomAgentLabels, roomWorkingId]);
  const activeFallbackModelDisplayName = useMemo(() => {
    if (!activeModelFallback) {
      return null;
    }
    return formatModelDisplayName(
      activeModelFallback.activeModel,
      modelOptionsByInstance.get(selectedInstanceId) ?? [],
    );
  }, [activeModelFallback, modelOptionsByInstance, selectedInstanceId]);

  // ------------------------------------------------------------------
  // Context window
  // ------------------------------------------------------------------
  // See recipientAgentId below: in a room it follows the agent being addressed.

  // ------------------------------------------------------------------
  // Composer-local state
  // ------------------------------------------------------------------
  const [composerCursor, setComposerCursor] = useState(() =>
    collapseExpandedComposerCursor(prompt, prompt.length),
  );
  const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
    detectComposerTrigger(prompt, prompt.length),
  );
  const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
  const [composerHighlightedSearchKey, setComposerHighlightedSearchKey] = useState<string | null>(
    null,
  );
  const [isDragOverComposer, setIsDragOverComposer] = useState(false);
  const [composerFooterTier, setComposerFooterTier] = useState<ComposerFooterTier>("full");
  const isComposerFooterCompact = composerFooterTier === "compact";
  const isComposerFooterIconOnly = composerFooterTier === "icons";
  const [isComposerPrimaryActionsCompact, setIsComposerPrimaryActionsCompact] = useState(false);
  const [isComposerModelPickerOpen, setIsComposerModelPickerOpen] = useState(false);
  const [isGoalEditorOpen, setIsGoalEditorOpen] = useState(false);
  const goalBarVisible = goalSupported && (threadGoal !== null || isGoalEditorOpen);
  // Commands this thread will act on: the built-ins plus whatever the selected
  // provider reports. The editor tints the leading token so typed-past-the-menu
  // commands still read as commands.
  const recognizedSlashCommands = useMemo(
    () => [
      "plan",
      "default",
      ...(goalSupported ? ["goal"] : []),
      ...(selectedProviderStatus?.slashCommands ?? []).map((command) => command.name),
    ],
    [goalSupported, selectedProviderStatus],
  );

  useEffect(() => {
    setIsGoalEditorOpen(false);
  }, [activeThreadId]);
  const [, setIsComposerFocused] = useState(false);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const isMobileViewport = useMediaQuery("max-sm");
  // Keep the real editor mounted on phones; the one-line proxy is fragile with mobile browser focus.
  const isComposerCollapsedMobile = false;
  const canCaptureScreenshot =
    typeof window !== "undefined" && typeof window.desktopBridge?.captureScreenshot === "function";

  // ------------------------------------------------------------------
  // Refs
  // ------------------------------------------------------------------
  const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const attachmentFileInputRef = useRef<HTMLInputElement>(null);
  const composerFormRef = useRef<HTMLFormElement>(null);
  const composerSurfaceRef = useRef<HTMLDivElement>(null);
  const composerFormHeightRef = useRef(0);
  const composerFooterLeftActionsRef = useRef<HTMLDivElement>(null);
  const composerFooterTierStateRef = useRef(INITIAL_COMPOSER_FOOTER_TIER_STATE);
  const composerFooterContentKeyRef = useRef<string | null>(null);
  const composerSelectLockRef = useRef(false);
  const composerMenuOpenRef = useRef(false);
  const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
  const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);
  const composerBlurFrameRef = useRef<number | null>(null);
  const mobileComposerExpandFrameRef = useRef<number | null>(null);
  const mobileComposerExpandReleaseFrameRef = useRef<number | null>(null);
  const mobileComposerExpandInFlightRef = useRef(false);
  const dragDepthRef = useRef(0);

  // ------------------------------------------------------------------
  // Derived: composer send state
  // ------------------------------------------------------------------
  const composerSendState = useMemo(
    () =>
      deriveComposerSendState({
        prompt,
        attachmentCount: composerAttachments.length,
        terminalContexts: composerTerminalContexts,
        transcriptHighlightContexts: composerTranscriptHighlightContexts,
        fileSelectionContextCount: composerFileSelectionContexts.length,
        pickedElementContextCount: composerPickedElementContexts.length,
        drawingContextCount: composerDrawingContexts.length,
      }),
    [
      composerAttachments.length,
      composerTerminalContexts,
      composerTranscriptHighlightContexts,
      composerFileSelectionContexts.length,
      composerPickedElementContexts.length,
      composerDrawingContexts.length,
      prompt,
    ],
  );

  // ------------------------------------------------------------------
  // Derived: composer trigger / menu
  // ------------------------------------------------------------------
  const composerTriggerKind = composerTrigger?.kind ?? null;
  const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
  const isPathTrigger = composerTriggerKind === "path" && canReferenceWorkspaceFiles;
  const [debouncedPathQuery, composerPathQueryDebouncer] = useDebouncedValue(
    pathTriggerQuery,
    { wait: COMPOSER_PATH_QUERY_DEBOUNCE_MS },
    (debouncerState) => ({ isPending: debouncerState.isPending }),
  );
  const effectivePathQuery = pathTriggerQuery.length > 0 ? debouncedPathQuery : "";
  const workspaceEntriesQuery = useQuery(
    projectSearchEntriesQueryOptions({
      environmentId,
      cwd: gitCwd,
      query: effectivePathQuery,
      enabled: isPathTrigger,
      allowEmptyQuery: true,
      limit: 80,
    }),
  );
  const workspaceEntries = workspaceEntriesQuery.data?.entries ?? EMPTY_PROJECT_ENTRIES;

  const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
    if (!composerTrigger) return [];
    if (composerTrigger.kind === "path") {
      const agentItems = matchRoomAgents(roomMentionAgents, composerTrigger.query).map(
        (agent): ComposerCommandItem => ({
          id: `agent:${roomAgentKey(agent.participantId)}`,
          type: "room-agent",
          participantId: agent.participantId,
          entry: agent.entry,
          label: agent.name,
          description: agent.status,
        }),
      );
      const pathItems = workspaceEntries.map((entry): ComposerCommandItem => ({
        id: `path:${entry.kind}:${entry.path}`,
        type: "path",
        path: entry.path,
        pathKind: entry.kind,
        label: basenameOfPath(entry.path),
        description: entry.parentPath ?? "",
      }));
      return [...agentItems, ...pathItems];
    }
    if (composerTrigger.kind === "slash-command") {
      const builtInSlashCommandItems = [
        {
          id: "slash:model",
          type: "slash-command",
          command: "model",
          label: "/model",
          description: "Switch response model for this thread",
        },
        ...(goalSupported
          ? [
              {
                id: "slash:goal",
                type: "slash-command",
                command: "goal",
                label: "/goal",
                description: threadGoal
                  ? "Edit this thread's long-running goal"
                  : "Set a long-running goal for this thread",
              } satisfies Extract<ComposerCommandItem, { type: "slash-command" }>,
            ]
          : []),
        {
          id: "slash:plan",
          type: "slash-command",
          command: "plan",
          label: "/plan",
          description: "Switch this thread into plan mode",
        },
        {
          id: "slash:default",
          type: "slash-command",
          command: "default",
          label: "/default",
          description: "Switch this thread back to normal build mode",
        },
      ] satisfies ReadonlyArray<Extract<ComposerCommandItem, { type: "slash-command" }>>;
      const providerSlashCommandItems = (selectedProviderStatus?.slashCommands ?? [])
        // Claude lists its own /goal; the goal bar's stands in for it.
        .filter((command) => !(goalSupported && command.name === "goal"))
        .map((command) => ({
          id: `provider-slash-command:${selectedProvider}:${command.name}`,
          type: "provider-slash-command" as const,
          provider: selectedProvider,
          command,
          label: `/${command.name}`,
          description: command.description ?? command.input?.hint ?? "Run provider command",
        }));
      const query = composerTrigger.query.trim().toLowerCase();
      const slashCommandItems = [...builtInSlashCommandItems, ...providerSlashCommandItems];
      const rankedCommandItems = query
        ? searchSlashCommandItems(slashCommandItems, query)
        : slashCommandItems;
      // Skills are ranked in their own list and appended after the commands —
      // the two kinds score on different scales, so a shared ranker would
      // interleave them arbitrarily.
      const skillItems = buildComposerSkillItems(
        searchProviderSkills(composerSkills, query),
        selectedProvider,
      );
      // The menu shows labeled sections even while filtering, so keyboard
      // order must match section order: built-ins, provider commands, plugin
      // skills, skills. Ranking is preserved within each section.
      return [
        ...rankedCommandItems.filter((item) => item.type === "slash-command"),
        ...rankedCommandItems.filter(
          (item) =>
            item.type === "provider-slash-command" && !isPluginSkillCommandName(item.command.name),
        ),
        ...rankedCommandItems.filter(
          (item) =>
            item.type === "provider-slash-command" && isPluginSkillCommandName(item.command.name),
        ),
        ...skillItems,
      ];
    }
    if (composerTrigger.kind === "skill") {
      return buildComposerSkillItems(
        searchProviderSkills(composerSkills, composerTrigger.query),
        selectedProvider,
      );
    }
    return [];
  }, [
    composerSkills,
    composerTrigger,
    goalSupported,
    selectedProvider,
    selectedProviderStatus,
    threadGoal,
    workspaceEntries,
  ]);

  const composerMenuOpen = Boolean(composerTrigger);
  const composerMenuAnchorRef = useRef<HTMLDivElement>(null);
  const composerMenuRoom = useComposerMenuRoom(composerMenuAnchorRef, composerMenuOpen);
  const composerMenuSearchKey = composerTrigger
    ? `${composerTrigger.kind}:${composerTrigger.query.trim().toLowerCase()}`
    : null;
  const activeComposerMenuItem = useMemo(() => {
    const activeItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    return composerMenuItems.find((item) => item.id === activeItemId) ?? null;
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuSearchKey,
  ]);

  composerMenuOpenRef.current = composerMenuOpen;
  composerMenuItemsRef.current = composerMenuItems;
  activeComposerMenuItemRef.current = activeComposerMenuItem;

  const nonPersistedComposerImageIdSet = useMemo(
    () => new Set(nonPersistedComposerImageIds),
    [nonPersistedComposerImageIds],
  );

  const isComposerApprovalState = activePendingApproval !== null;
  const hasBlockingQuestion = pendingUserInputs.some((input) => input.isBlocking !== false);
  // A blocking question owns the composer: the editor and toolbar give way to a
  // thin row that keeps the saved draft visible and Stop reachable.
  const isComposerBlockedByQuestion = hasBlockingQuestion && !isComposerApprovalState;
  const hasActiveTurn =
    phase === "running" ||
    activeThread?.session?.activeTurnId != null ||
    activeThread?.latestTurn?.state === "running";
  // A settled turn can leave the thread live: it waits on a command or an
  // agent. Stop must still reach that, or a thread whose agent can't answer
  // (a usage limit, an error) stays live with no way to end it.
  const isWaitingOnBackgroundWork =
    !hasActiveTurn &&
    isWaitingOnBackgroundTasks(activeThread?.latestTurn ?? null, activeThread?.session ?? null);
  const hasComposerHeader =
    isComposerApprovalState ||
    pendingUserInputs.length > 0 ||
    (showPlanFollowUpPrompt && activeProposedPlan !== null);
  const showCollapsedMobilePromptRow =
    isComposerCollapsedMobile && !isComposerApprovalState && pendingUserInputs.length === 0;
  // Turn whose suggestion the user already responded to. Suppresses the stale
  // suggestion from flashing back over an empty composer while the next turn is
  // connecting / starting (before it registers as the latest turn).
  const [dismissedSuggestionTurnId, setDismissedSuggestionTurnId] = useState<TurnId | null>(null);
  const queuedFollowUpCount = activeThread?.queuedFollowUps?.length ?? 0;

  const composerFooterHasWideActions = showPlanFollowUpPrompt;
  const composerFooterActionLayoutKey = useMemo(() => {
    if (phase === "running") {
      return `running:${composerSendState.hasSendableContent}:${isSendBusy}:${isConnecting}`;
    }
    if (showPlanFollowUpPrompt) {
      return prompt.trim().length > 0
        ? "plan:refine"
        : `plan:implement:${isWaitingOnBackgroundWork}`;
    }
    return `idle:${composerSendState.hasSendableContent}:${isSendBusy}:${isConnecting}:${isPreparingWorktree}`;
  }, [
    composerSendState.hasSendableContent,
    isConnecting,
    isPreparingWorktree,
    isSendBusy,
    isWaitingOnBackgroundWork,
    phase,
    prompt,
    showPlanFollowUpPrompt,
  ]);

  const isComposerMenuLoading =
    (isPathTrigger &&
      ((pathTriggerQuery.length > 0 && composerPathQueryDebouncer.state.isPending) ||
        workspaceEntriesQuery.isLoading ||
        workspaceEntriesQuery.isFetching)) ||
    (composerTriggerKind === "skill" &&
      (projectSkillsQuery.isLoading || projectSkillsQuery.isFetching));
  const composerMenuEmptyState = useMemo(() => {
    if (composerTriggerKind === "skill") {
      if (projectSkillsQuery.isError) {
        return "Could not load skills for this project and provider.";
      }
      return "No skills found. Try / to browse provider commands.";
    }
    if (composerTriggerKind === "path") {
      if (!canReferenceWorkspaceFiles) {
        return "Continue this chat in a project to reference workspace files.";
      }
      if (workspaceEntriesQuery.isError) {
        return "Could not load files and folders from this workspace.";
      }
      return pathTriggerQuery.trim().length === 0
        ? "No files or folders are available in this workspace."
        : "No matching files or folders.";
    }
    return "No matching command.";
  }, [
    composerTriggerKind,
    canReferenceWorkspaceFiles,
    pathTriggerQuery,
    projectSkillsQuery.isError,
    workspaceEntriesQuery.isError,
  ]);
  const composerCapabilityHint = useMemo(
    () =>
      buildComposerCapabilityHint({
        canReferenceFiles: canReferenceWorkspaceFiles,
        canInvokeSkills: composerSkills.some((skill) => skill.enabled),
        canMentionAgents: roomMentionAgents.length > 0,
      }),
    [canReferenceWorkspaceFiles, composerSkills, roomMentionAgents.length],
  );
  const defaultComposerPlaceholder = useMemo(
    () =>
      buildDefaultComposerPlaceholder({
        canReferenceFiles: canReferenceWorkspaceFiles,
        canInvokeSkills: composerSkills.some((skill) => skill.enabled),
        canMentionAgents: roomMentionAgents.length > 0,
        recipientName: roomsShown && roomAgents.inRoom ? roomAgents.recipient.name : undefined,
      }),
    [
      canReferenceWorkspaceFiles,
      composerSkills,
      roomAgents.inRoom,
      roomAgents.recipient.name,
      roomMentionAgents.length,
      roomsShown,
    ],
  );

  // ------------------------------------------------------------------
  // Provider traits UI
  // ------------------------------------------------------------------
  const providerTraitsMenuContent = renderProviderTraitsMenuContent({
    provider: selectedProvider,
    instanceId: selectedInstanceId,
    ...(routeKind === "server" ? { threadRef: routeThreadRef } : {}),
    ...(routeKind === "draft" && draftId ? { draftId } : {}),
    model: selectedModel,
    models: selectedProviderModels,
    modelOptions: composerModelOptions?.[selectedInstanceId],
  });
  const ownWorkflowRunning = (activeThread?.workflowRuns ?? []).some(
    (run) => run.status === "running" && run.owner.participantId === null,
  );
  const providerTraitsPicker = renderProviderTraitsPicker({
    provider: selectedProvider,
    instanceId: selectedInstanceId,
    ...(routeKind === "server" ? { threadRef: routeThreadRef } : {}),
    ...(routeKind === "draft" && draftId ? { draftId } : {}),
    model: selectedModel,
    models: selectedProviderModels,
    modelOptions: composerModelOptions?.[selectedInstanceId],
    iconOnly: composerFooterTier !== "full",
    // In a room, only while the thread's own agent is the one working; and
    // while a workflow the thread's own agent started is still going, since
    // that is Ultracode's work too after the turn has ended.
    working: (hasActiveTurn && roomWorkingId == null) || ownWorkflowRunning,
  });
  // An added agent keeps its own reasoning and the like. Picking them here
  // holds them until the next turn for that agent, which carries them.
  const addressedRoomAgent =
    addressingRoomAgent && activeThread
      ? (activeThread.participants ?? []).find((entry) => entry.id === roomRecipientId)
      : undefined;
  const addressedRoomAgentEntry = addressedRoomAgent
    ? providerInstanceEntries.find(
        (entry) => entry.instanceId === addressedRoomAgent.modelSelection.instanceId,
      )
    : undefined;
  const pendingRoomAgentOptions = useRoomAgentOptions(routeThreadRef, roomRecipientId);
  const roomAgentTraitsProps =
    addressedRoomAgent && addressedRoomAgentEntry
      ? {
          provider: addressedRoomAgentEntry.driverKind,
          instanceId: addressedRoomAgent.modelSelection.instanceId,
          models: addressedRoomAgentEntry.models,
          model: addressedRoomAgent.modelSelection.model,
          modelOptions: pendingRoomAgentOptions ?? addressedRoomAgent.modelSelection.options,
          working: roomWorkingId != null && roomWorkingId === addressedRoomAgent.id,
          onModelOptionsChange: (next: ReadonlyArray<ProviderOptionSelection> | undefined) =>
            roomAgents.pickOptions(addressedRoomAgent.id, next ?? []),
        }
      : null;
  const showRoomAgentTraits =
    roomAgentTraitsProps !== null && shouldRenderTraitsControls(roomAgentTraitsProps);

  // The access level is the thread's, and each agent runs it at what its own
  // agent and model offer, so the controls describe the agent the next message
  // goes to: an addressed room agent, else the thread's own.
  const accessAgentInstanceId =
    addressedRoomAgent && addressedRoomAgentEntry ? addressedRoomAgentEntry.instanceId : null;
  const accessAgentModel = addressedRoomAgent?.modelSelection.model ?? null;
  const accessLevel = useMemo(() => {
    const entry =
      accessAgentInstanceId === null
        ? undefined
        : providerInstanceEntries.find(
            (candidate) => candidate.instanceId === accessAgentInstanceId,
          );
    return deriveAccessLevel({
      providers: providerStatuses,
      provider: entry?.driverKind ?? selectedProvider,
      snapshot: entry ? entry.snapshot : selectedProviderStatus,
      models: entry?.models ?? selectedProviderModels,
      model: entry ? accessAgentModel : selectedModel,
      mode: runtimeMode,
    });
  }, [
    accessAgentInstanceId,
    accessAgentModel,
    providerInstanceEntries,
    providerStatuses,
    runtimeMode,
    selectedModel,
    selectedProvider,
    selectedProviderModels,
    selectedProviderStatus,
  ]);

  // The context window meter, its usage card, and the prompt suggestion
  // describe the agent the next message goes to: in a room, an added agent's
  // own context, its window, its provider's plan usage, and its suggestion.
  const recipientAgentId = addressedRoomAgent?.id ?? null;
  // Any other agent that took part, a guest brought in for one review
  // included, leaves records of its own that are not this agent's.
  const threadHasOtherAgents = hasAgentRecords({
    participants: activeThread?.participants ?? [],
  });
  const threadMessages = activeThread?.messages;
  // Undefined while the thread's own agent is the only one: every record is its own.
  const isFromRecipientAgent = useMemo(() => {
    if (!threadHasOtherAgents) {
      return undefined;
    }
    const turnOwners = roomTurnOwners(threadMessages ?? []);
    return (activity: OrchestrationThreadActivity) =>
      roomActivityAgent(activity, turnOwners) === recipientAgentId;
  }, [recipientAgentId, threadHasOtherAgents, threadMessages]);
  const activeContextWindow = useMemo(
    () => deriveLatestContextWindowSnapshot(activeThreadActivities ?? [], isFromRecipientAgent),
    [activeThreadActivities, isFromRecipientAgent],
  );
  const sideAnswerInProgress = (activeThread?.sideTurn ?? null) !== null;
  const recipientDriverKind = addressedRoomAgent
    ? addressedRoomAgentEntry?.driverKind
    : selectedProvider;
  const promptSuggestion = useMemo(
    () =>
      selectPromptSuggestion({
        recipientDriverKind,
        isFromRecipient: isFromRecipientAgent,
        phase,
        sideAnswerInProgress,
        isSendBusy,
        hasComposerApproval: isComposerApprovalState,
        pendingUserInputCount: pendingUserInputs.length,
        showPlanFollowUpPrompt,
        queuedMessageCount: queuedFollowUpCount,
        latestTurn: activeThread?.latestTurn ?? null,
        dismissedTurnId: dismissedSuggestionTurnId,
        activities: activeThreadActivities ?? [],
      }),
    [
      activeThread?.latestTurn,
      activeThreadActivities,
      dismissedSuggestionTurnId,
      isComposerApprovalState,
      isFromRecipientAgent,
      isSendBusy,
      pendingUserInputs.length,
      phase,
      queuedFollowUpCount,
      recipientDriverKind,
      showPlanFollowUpPrompt,
      sideAnswerInProgress,
    ],
  );
  const promptSuggestionView = useMemo(
    () =>
      promptSuggestion
        ? resolvePromptSuggestionView({
            prompt,
            cursor: composerCursor,
            suggestion: promptSuggestion.text,
          })
        : null,
    [composerCursor, prompt, promptSuggestion],
  );
  const meterContextWindowLabel = roomAgentTraitsProps
    ? getComposerProviderState({
        provider: roomAgentTraitsProps.provider,
        model: roomAgentTraitsProps.model,
        models: roomAgentTraitsProps.models,
        prompt,
        modelOptions: roomAgentTraitsProps.modelOptions,
      }).contextWindowLabel
    : composerProviderState.contextWindowLabel;
  const meterProviderStatus = addressedRoomAgentEntry
    ? (addressedRoomAgentEntry.snapshot ?? null)
    : selectedProviderStatus;
  const meterInstanceId = addressedRoomAgent?.modelSelection.instanceId ?? selectedInstanceId;
  const meterProviderLabel = addressedRoomAgentEntry?.displayName ?? selectedProviderDisplayName;
  // Account-level usage (5h/weekly windows) for that agent's provider.
  const meterProviderAccountUsage = useMemo(
    () => deriveProviderAccountUsagePresentationForProvider(meterProviderStatus),
    [meterProviderStatus],
  );
  const meterProviderResetCredits =
    meterProviderStatus?.accountUsage?.rateLimitResetCredits ?? null;
  const canResetMeterProviderUsage = canRequestProviderRateLimitResetCredit(
    meterProviderStatus,
    meterProviderResetCredits?.availableCount,
  );
  const requestMeterProviderUsageReset = useCallback(() => {
    if (!canResetMeterProviderUsage || !meterProviderResetCredits) return;
    requestRateLimitResetCredit({
      instanceId: meterInstanceId,
      providerLabel: meterProviderLabel,
      resetCredits: meterProviderResetCredits,
    });
  }, [
    canResetMeterProviderUsage,
    meterInstanceId,
    meterProviderLabel,
    meterProviderResetCredits,
    requestRateLimitResetCredit,
  ]);
  // Compacting works on the agent holding the thread; the meter offers it
  // only while it shows that agent.
  const meterShowsSlotHolder = recipientAgentId === (activeThread?.session?.participantId ?? null);
  const collapsedComposerPrimaryActionDisabled =
    isSendBusy || isConnecting || !composerSendState.hasSendableContent;
  const followUpDelivery = useSettings((settings) => settings.followUpDelivery);
  const { updateSettings } = useUpdateSettings();
  const handleFollowUpDeliveryChange = useCallback(
    (delivery: FollowUpDelivery) => updateSettings({ followUpDelivery: delivery }),
    [updateSettings],
  );
  // Rooms: while another agent works, a message for this one is asked now
  // (answered read-only on the side) or, when picked for this message, waits
  // until the other finishes. The send path reads the same rule
  // (resolveRoomDelivery).
  const roomHolderId = activeThread?.session?.participantId ?? null;
  const roomRecipientDriverKind = providerInstanceEntries.find(
    (entry) =>
      entry.instanceId ===
      (addressedRoomAgent?.modelSelection ?? activeThread?.modelSelection)?.instanceId,
  )?.driverKind;
  const roomDeliveryMode =
    roomAgentLabels !== null
      ? resolveRoomDelivery({
          recipientId: roomRecipientId,
          holderId: roomHolderId,
          holderBusy:
            hasActiveTurn ||
            isWaitingOnBackgroundTasks(
              activeThread?.latestTurn ?? null,
              activeThread?.session ?? null,
            ),
          recipientDriverKind: roomRecipientDriverKind,
          waitChosen: roomWaitChosen,
        })
      : "direct";
  const roomDelivery = useMemo<ComposerRoomDelivery | null>(
    () =>
      roomAgentLabels === null || roomDeliveryMode === "direct"
        ? null
        : {
            mode: roomDeliveryMode,
            canAsk: canAnswerOnTheSide(roomRecipientDriverKind),
            recipientName: roomAgentLabels.get(roomAgentKey(roomRecipientId))?.name ?? "this agent",
            holderName:
              roomAgentLabels.get(roomAgentKey(roomHolderId))?.name ?? "the agent at work",
          },
    [roomAgentLabels, roomDeliveryMode, roomHolderId, roomRecipientDriverKind, roomRecipientId],
  );
  const collapsedComposerPrimaryActionLabel = roomDelivery
    ? roomDeliveryLabels(roomDelivery).action
    : phase === "running"
      ? FOLLOW_UP_DELIVERY_LABELS[followUpDelivery].action
      : "Send message";
  // Shared gate for every "Add" action (upload + screenshot). The in-flight
  // capture only blocks the screenshot item, not uploading images, so it is
  // handled inside the menu rather than here. Models without image input
  // still accept document attachments, so modality no longer disables the
  // whole menu — image files are rejected per-file during ingest instead.
  const attachmentsDisabled = hasBlockingQuestion;
  const attachmentsDisabledReason = attachmentsDisabled
    ? "Finish the pending prompt before adding attachments"
    : null;
  // Nothing can be sent from here: the thread's computer is out of reach, or
  // the thread is moving or moved away.
  const composerUnavailable = environmentUnavailable !== null || threadLocked !== null;
  // Dictation writes into the prompt, so it is blocked by everything that
  // blocks typing, named the way the placeholder names it.
  const dictationDisabledReason = hasBlockingQuestion
    ? isComposerApprovalState
      ? "Resolve the approval first"
      : "Answer the question first"
    : environmentUnavailable
      ? `${environmentUnavailable.label} is ${
          environmentUnavailable.connectionState === "connecting" ? "connecting" : "disconnected"
        }`
      : (threadLocked?.reason ?? null);

  // ------------------------------------------------------------------
  // Prompt helpers
  // ------------------------------------------------------------------
  const setPrompt = useCallback(
    (nextPrompt: string) => {
      setComposerDraftPrompt(composerDraftTarget, nextPrompt);
    },
    [composerDraftTarget, setComposerDraftPrompt],
  );

  const addComposerAttachment = useCallback(
    (image: ComposerAttachment) => {
      addComposerDraftAttachment(composerDraftTarget, image);
    },
    [composerDraftTarget, addComposerDraftAttachment],
  );

  const addComposerAttachmentsToDraft = useCallback(
    (images: ComposerAttachment[]) => {
      addComposerDraftAttachments(composerDraftTarget, images);
    },
    [composerDraftTarget, addComposerDraftAttachments],
  );

  const removeComposerAttachmentFromDraft = useCallback(
    (imageId: string) => {
      removeComposerDraftAttachment(composerDraftTarget, imageId);
    },
    [composerDraftTarget, removeComposerDraftAttachment],
  );

  // ------------------------------------------------------------------
  // Prompt stash
  // ------------------------------------------------------------------
  // One global queue. A stashed prompt carries text, attachments and the
  // context chips that survive a move, so "stash here, restore there" works
  // across threads and providers. Deliberately no model or provider: the
  // composer keeps whatever is selected right now.
  const stashEntries = usePromptStashStore((state) => state.entries);
  const stashEntryToQueue = usePromptStashStore((state) => state.stashEntry);
  const takeStashEntry = usePromptStashStore((state) => state.takeEntry);
  const finalizeStashEntryAttachments = usePromptStashStore(
    (state) => state.finalizeEntryAttachments,
  );
  const [isStashMenuOpen, setIsStashMenuOpen] = useState(false);
  const stashInFlightRef = useRef<Set<string>>(new Set());
  const stashShortcutLabel = useMemo(
    () =>
      shortcutLabelForCommand(keybindings, "composer.stash", {
        context: { terminalFocus: false },
      }),
    [keybindings],
  );

  // Chips alone are worth stashing: a set of picked elements and file
  // selections is as much work to reassemble as the sentence about them.
  const composerHasStashableContent =
    prompt.trim().length > 0 ||
    composerAttachments.length > 0 ||
    composerTerminalContexts.length > 0 ||
    composerTranscriptHighlightContexts.length > 0 ||
    composerFileSelectionContexts.length > 0 ||
    composerPickedElementContexts.length > 0;

  const restoreStashEntry = useCallback(
    (entry: PromptStashEntry) => {
      // Remove first so a double activation (click + Enter) cannot restore twice.
      const { entry: taken, durable } = takeStashEntry(entry.id);
      if (!taken) return;
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Restored prompt may reappear in the stash",
          description:
            "Browser storage rejected the update, so this entry could still be there after a reload.",
          data: { hideCopyButton: true },
        });
      }
      setIsStashMenuOpen(false);

      const currentPrompt = promptRef.current;
      // An attachment-only or chip-only stash must not append blank lines to
      // whatever is already in the composer.
      const nextPrompt =
        entry.prompt.length === 0
          ? currentPrompt
          : currentPrompt.trim().length
            ? `${currentPrompt.replace(/\s+$/, "")}\n\n${entry.prompt}`
            : entry.prompt;
      const promptChanged = nextPrompt !== currentPrompt;
      if (promptChanged) {
        promptRef.current = nextPrompt;
        setPrompt(nextPrompt);
        setComposerCursor(collapseExpandedComposerCursor(nextPrompt, nextPrompt.length));
        setComposerTrigger(null);
      }

      let unrestoredAttachmentNames: string[] = [];
      if (entry.attachments.length > 0) {
        const existingAttachments = composerAttachmentsRef.current;
        const existingIds = new Set(existingAttachments.map((attachment) => attachment.id));
        // The draft store also dedupes by mimeType+sizeBytes+name, so filter
        // on the same key here. Counting a duplicate against capacity would
        // burn a slot the store then refuses to fill, pushing a genuinely
        // unique attachment into the overflow list for nothing.
        const existingDedupKeys = new Set(
          existingAttachments.map((attachment) => composerAttachmentDedupKey(attachment)),
        );
        const capacity = Math.max(
          0,
          PROVIDER_SEND_TURN_MAX_ATTACHMENTS - existingAttachments.length,
        );
        const pending = entry.attachments.filter(
          (attachment) =>
            !existingIds.has(attachment.id) &&
            !existingDedupKeys.has(composerAttachmentDedupKey(attachment)),
        );
        // Anything past the attachment limit cannot be restored. The entry is
        // already out of the queue, so report the overflow by name instead of
        // discarding it silently.
        unrestoredAttachmentNames = pending.slice(capacity).map((attachment) => attachment.name);
        const restored = hydrateAttachmentsFromPersisted(pending.slice(0, capacity));
        if (restored.length > 0) {
          addComposerDraftAttachments(composerDraftTarget, restored);
        }
      }

      // Chips go back through the store's own add paths, so each one is
      // re-stamped for this thread and deduped against what is already there.
      const chips = stashEntryContextDrafts(taken);
      if (chips.terminalContexts.length > 0) {
        addComposerDraftTerminalContexts(composerDraftTarget, chips.terminalContexts);
      }
      if (chips.transcriptHighlightContexts.length > 0) {
        addComposerDraftTranscriptHighlightContexts(
          composerDraftTarget,
          chips.transcriptHighlightContexts,
        );
      }
      for (const context of chips.fileSelectionContexts) {
        addComposerDraftFileSelectionContext(composerDraftTarget, context);
      }
      if (chips.pickedElementContexts.length > 0) {
        // Picked elements are set as a whole list, so merge rather than
        // replace, and drop anything already pinned to the same element.
        const existingPicked = composerPickedElementContextsRef.current;
        const existingKeys = new Set(
          existingPicked.map((context) => pickedElementContextDedupKey(context)),
        );
        const additions = chips.pickedElementContexts.filter((context) => {
          const key = pickedElementContextDedupKey(context);
          if (existingKeys.has(key)) return false;
          existingKeys.add(key);
          return true;
        });
        if (additions.length > 0) {
          commitPickedElementContexts([...existingPicked, ...additions]);
        }
      }

      // Each cause gets its own sentence so "too large" is never blamed for a
      // file that actually failed to read, or for one the composer simply had
      // no room to take back.
      const missingReasons: string[] = [];
      if (taken.droppedAttachmentNames.length > 0) {
        missingReasons.push(
          `${taken.droppedAttachmentNames.join(", ")} exceeded the stash size limit when this prompt was saved.`,
        );
      }
      if (taken.unreadableAttachmentNames && taken.unreadableAttachmentNames.length > 0) {
        missingReasons.push(
          `${taken.unreadableAttachmentNames.join(", ")} could not be read when this prompt was saved.`,
        );
      }
      if (unrestoredAttachmentNames.length > 0) {
        missingReasons.push(
          `${unrestoredAttachmentNames.join(", ")} could not be restored: the composer is at its ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS}-attachment limit.`,
        );
      }
      if (missingReasons.length > 0) {
        toastManager.add({
          type: "warning",
          title: "Some attachments were not restored",
          description: missingReasons.join(" "),
        });
      }

      // Only yank the caret to the end when text was actually inserted;
      // restoring attachments alone should leave the user where they were.
      if (promptChanged) {
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAtEnd();
        });
      }
    },
    [
      addComposerDraftAttachments,
      addComposerDraftFileSelectionContext,
      addComposerDraftTerminalContexts,
      addComposerDraftTranscriptHighlightContexts,
      commitPickedElementContexts,
      composerAttachmentsRef,
      composerDraftTarget,
      composerPickedElementContextsRef,
      promptRef,
      setPrompt,
      takeStashEntry,
    ],
  );

  const deleteStashEntry = useCallback(
    (entry: PromptStashEntry) => {
      const { durable } = takeStashEntry(entry.id);
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Stash entry may come back",
          description:
            "Browser storage rejected the delete, so this prompt could reappear after a reload.",
          data: { hideCopyButton: true },
        });
      }
    },
    [takeStashEntry],
  );

  const stashCurrentPrompt = useCallback(async () => {
    // Inline terminal placeholders stay in the stashed text: their contexts
    // travel with the entry, and the n-th placeholder still maps to the n-th
    // context once both are appended back in order.
    const stashedPrompt = promptRef.current.trim();
    const attachments = [...composerAttachmentsRef.current];
    const contexts = buildStashEntryContexts({
      terminalContexts: composerTerminalContextsRef.current,
      transcriptHighlightContexts: composerTranscriptHighlightContextsRef.current,
      fileSelectionContexts: composerFileSelectionContextsRef.current,
      pickedElementContexts: composerPickedElementContextsRef.current,
    });
    const hasChips = Object.keys(contexts).length > 0;
    if (stashedPrompt.length === 0 && attachments.length === 0 && !hasChips) {
      setIsStashMenuOpen((open) => !open);
      return;
    }
    // A repeat press on the *same* still-unencoded snapshot would stash it
    // twice. Guard on the snapshot itself rather than a bare boolean: once
    // the composer has been cleared the user can type something genuinely new
    // (or switch threads) while encoding continues, and that deserves its own
    // entry.
    const snapshotKey = `${String(composerDraftTarget)}${stashedPrompt}${attachments
      .map((attachment) => attachment.id)
      .join(",")}`;
    if (stashInFlightRef.current.has(snapshotKey)) return;
    stashInFlightRef.current.add(snapshotKey);

    const stashTarget = composerDraftTarget;
    const entryId = randomUUID();
    try {
      // Persist the text-and-chips entry *first*, then clear. Ordering
      // matters in both directions: writing before clearing means a crash or
      // closed tab mid-encode still leaves the prompt recoverable, while
      // clearing before the async attachment work means edits typed during
      // encoding are not wiped. Attachments are appended to the stored entry
      // as they finish encoding.
      const { evicted, written, durable } = stashEntryToQueue({
        id: entryId,
        createdAt: new Date().toISOString(),
        prompt: stashedPrompt,
        attachments: [],
        droppedAttachmentNames: [],
        unreadableAttachmentNames: [],
        pendingAttachmentCount: attachments.length,
        ...contexts,
      });

      // Clearing the composer is only safe once the write actually landed.
      // If it was rejected (quota) the store has already rolled itself back,
      // so leave the composer untouched rather than making it the second
      // casualty of a reload.
      if (!written) {
        toastManager.add(
          failureNotice({
            title: "Couldn't stash this prompt",
            reason:
              "Browser storage rejected the write, so the composer was left as-is. Free up site data and try again.",
            data: { hideCopyButton: true },
          }),
        );
        return;
      }
      // Written but only into the in-memory fallback (localStorage blocked):
      // the entry is visible and restorable this session, so proceed with the
      // clear, but say it will not survive a reload.
      if (!durable) {
        toastManager.add({
          type: "warning",
          title: "Stashed prompt won't survive a reload",
          description:
            "Browser storage is unavailable, so this stash is kept only until you close this page.",
          data: { hideCopyButton: true },
        });
      }

      // Drawings are deliberately left in place: the stash cannot carry their
      // picture, so clearing them here would destroy them for good.
      promptRef.current = "";
      clearStashableComposerContent(stashTarget);
      setComposerCursor(0);
      setComposerTrigger(null);

      if (evicted) {
        toastManager.add({
          type: "warning",
          title: "Oldest stashed prompt discarded",
          description: `The stash holds ${MAX_STASH_ENTRIES} prompts; the oldest was removed to make room.`,
          data: { hideCopyButton: true },
        });
      }

      // Images are re-encoded for the stash rather than stored verbatim: the
      // composer allows large files, but localStorage gives the whole origin
      // roughly 5MB. Only the stashed copy shrinks; the live attachment (and
      // anything sent without stashing) keeps the original file.
      const candidateAttachments: PersistedComposerAttachment[] = [];
      const oversizedNames: string[] = [];
      const unreadableNames: string[] = [];
      for (const attachment of attachments) {
        const result = attachment.mimeType.startsWith("image/")
          ? await compressImageForStash(attachment.file)
          : await readFileForStash(attachment.file);
        if (!result.ok) {
          // "too large" and "could not be read" are distinct outcomes; the
          // restore toast reports them separately.
          (result.reason === "too-large" ? oversizedNames : unreadableNames).push(attachment.name);
          continue;
        }
        candidateAttachments.push({
          id: attachment.id,
          name: attachment.name,
          mimeType: result.image.mimeType,
          sizeBytes: result.image.sizeBytes,
          dataUrl: result.image.dataUrl,
        });
      }
      const { kept, droppedNames } = partitionStashAttachments(candidateAttachments);

      const { attached, durable: attachmentsDurable } = finalizeStashEntryAttachments(entryId, {
        attachments: kept,
        droppedAttachmentNames: [...oversizedNames, ...droppedNames],
        unreadableAttachmentNames: unreadableNames,
      });
      if (attached) {
        // The second phase can be rejected on its own: the text entry fit, but
        // adding attachment payloads pushed past the quota. Disk would then
        // still hold the phase-one entry with a pending count set, which reads
        // as an orphan after reload, so say so now. Gated on the entry write
        // having been durable: on the in-memory fallback nothing is ever
        // durable and the session-only warning already covered it.
        if (!attachmentsDurable && durable && attachments.length > 0) {
          toastManager.add({
            type: "warning",
            title: "Stashed attachments weren't saved",
            description:
              "The prompt was stashed, but browser storage rejected its attachments. They will be missing if you reload.",
            data: { hideCopyButton: true },
          });
        }
      } else if (kept.length > 0) {
        // The entry was restored or deleted before its attachments finished
        // encoding, so they have nowhere to land. Say so rather than letting
        // them evaporate.
        toastManager.add({
          type: "warning",
          title: "Stashed attachments didn't attach",
          description: `That prompt was restored or deleted before ${kept.length} attachment${kept.length === 1 ? "" : "s"} finished saving. Add ${kept.length === 1 ? "it" : "them"} again if you still need ${kept.length === 1 ? "it" : "them"}.`,
          data: { hideCopyButton: true },
        });
      }
    } finally {
      // Must clear on every path: a throw that left this set would wedge this
      // snapshot's shortcut until the composer remounts.
      stashInFlightRef.current.delete(snapshotKey);
    }
  }, [
    clearStashableComposerContent,
    composerAttachmentsRef,
    composerDraftTarget,
    composerFileSelectionContextsRef,
    composerPickedElementContextsRef,
    composerTerminalContextsRef,
    composerTranscriptHighlightContextsRef,
    finalizeStashEntryAttachments,
    promptRef,
    stashEntryToQueue,
  ]);

  const stashControlProps = useMemo(
    () => ({
      entries: stashEntries,
      open: isStashMenuOpen,
      onOpenChange: setIsStashMenuOpen,
      canStash: composerHasStashableContent && !attachmentsDisabled,
      stashShortcutLabel,
      onStash: () => {
        void stashCurrentPrompt();
      },
      onRestore: restoreStashEntry,
      onDelete: deleteStashEntry,
    }),
    [
      attachmentsDisabled,
      composerHasStashableContent,
      deleteStashEntry,
      isStashMenuOpen,
      restoreStashEntry,
      stashCurrentPrompt,
      stashEntries,
      stashShortcutLabel,
    ],
  );

  useEffect(() => {
    const handler = (event: globalThis.KeyboardEvent) => {
      const command = resolveShortcutCommand(event, keybindings, {
        context: {
          terminalFocus: isTerminalFocused(),
          terminalOpen,
          modelPickerOpen: isComposerModelPickerOpen,
        },
      });
      if (command !== "composer.stash") return;
      // Always claim the shortcut so the browser's save dialog never opens,
      // even when the composer is in a state that cannot stash.
      event.preventDefault();
      event.stopPropagation();
      if (useCommandPaletteStore.getState().open || attachmentsDisabled) {
        return;
      }
      void stashCurrentPrompt();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [
    attachmentsDisabled,
    isComposerModelPickerOpen,
    keybindings,
    stashCurrentPrompt,
    terminalOpen,
  ]);

  // The stash popup is a transient picker, not a panel: close it when the
  // trigger-driven command menu takes over the same layer, and when the user
  // resumes typing.
  useEffect(() => {
    if (composerMenuOpen) {
      setIsStashMenuOpen(false);
    }
  }, [composerMenuOpen]);
  useEffect(() => {
    setIsStashMenuOpen(false);
  }, [prompt]);

  const removeComposerTerminalContextFromDraft = useCallback(
    (contextId: string) => {
      const contextIndex = composerTerminalContexts.findIndex(
        (context) => context.id === contextId,
      );
      if (contextIndex < 0) return;
      const removal = removeInlineTerminalContextPlaceholder(promptRef.current, contextIndex);
      removeComposerDraftTerminalContext(composerDraftTarget, contextId);
      if (removal.prompt !== promptRef.current) {
        promptRef.current = removal.prompt;
        setPrompt(removal.prompt);
        const nextCursor = collapseExpandedComposerCursor(removal.prompt, removal.cursor);
        setComposerCursor(nextCursor);
        setComposerTrigger(detectComposerTrigger(removal.prompt, removal.cursor));
      }
    },
    [
      composerDraftTarget,
      composerTerminalContexts,
      promptRef,
      removeComposerDraftTerminalContext,
      setPrompt,
    ],
  );

  const removeComposerFileSelectionContextFromDraft = useCallback(
    (contextId: string) => {
      removeComposerDraftFileSelectionContext(composerDraftTarget, contextId);
    },
    [composerDraftTarget, removeComposerDraftFileSelectionContext],
  );

  const removeComposerTranscriptHighlightContextFromDraft = useCallback(
    (contextId: string) => {
      removeComposerDraftTranscriptHighlightContext(composerDraftTarget, contextId);
    },
    [composerDraftTarget, removeComposerDraftTranscriptHighlightContext],
  );

  const updateComposerTranscriptHighlightContextNote = useCallback(
    (contextId: string, note: string) => {
      updateComposerDraftTranscriptHighlightContextNote(composerDraftTarget, contextId, note);
    },
    [composerDraftTarget, updateComposerDraftTranscriptHighlightContextNote],
  );

  // ------------------------------------------------------------------
  // Sync refs back to parent
  // ------------------------------------------------------------------
  useEffect(() => {
    promptRef.current = prompt;
    setComposerCursor((existing) => clampCollapsedComposerCursor(prompt, existing));
  }, [prompt, promptRef]);

  useEffect(() => {
    composerAttachmentsRef.current = composerAttachments;
  }, [composerAttachments, composerAttachmentsRef]);

  useEffect(() => {
    composerTerminalContextsRef.current = composerTerminalContexts;
  }, [composerTerminalContexts, composerTerminalContextsRef]);

  useEffect(() => {
    composerTranscriptHighlightContextsRef.current = composerTranscriptHighlightContexts;
  }, [composerTranscriptHighlightContexts, composerTranscriptHighlightContextsRef]);
  useEffect(() => {
    composerFileSelectionContextsRef.current = composerFileSelectionContexts;
  }, [composerFileSelectionContexts, composerFileSelectionContextsRef]);

  useEffect(() => {
    composerPickedElementContextsRef.current = composerPickedElementContexts;
  }, [composerPickedElementContexts, composerPickedElementContextsRef]);

  // ------------------------------------------------------------------
  // Composer menu highlight sync
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!composerMenuOpen) {
      setComposerHighlightedItemId(null);
      setComposerHighlightedSearchKey(null);
      return;
    }
    const nextActiveItemId = resolveComposerMenuActiveItemId({
      items: composerMenuItems,
      highlightedItemId: composerHighlightedItemId,
      currentSearchKey: composerMenuSearchKey,
      highlightedSearchKey: composerHighlightedSearchKey,
    });
    setComposerHighlightedItemId((existing) =>
      existing === nextActiveItemId ? existing : nextActiveItemId,
    );
    setComposerHighlightedSearchKey((existing) =>
      existing === composerMenuSearchKey ? existing : composerMenuSearchKey,
    );
  }, [
    composerHighlightedItemId,
    composerHighlightedSearchKey,
    composerMenuItems,
    composerMenuOpen,
    composerMenuSearchKey,
  ]);

  // ------------------------------------------------------------------
  // Reset compositor state on thread/draft change
  // ------------------------------------------------------------------
  useEffect(() => {
    setComposerHighlightedItemId(null);
    setComposerCursor(collapseExpandedComposerCursor(promptRef.current, promptRef.current.length));
    setComposerTrigger(detectComposerTrigger(promptRef.current, promptRef.current.length));
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
  }, [draftId, activeThreadId, promptRef]);

  // ------------------------------------------------------------------
  // Footer layout observation
  // ------------------------------------------------------------------
  // Left-hand footer content whose width the tier measurement depends on.
  // When it changes, the recorded overflow widths describe labels that are
  // gone, so the roomier tiers get retried at the current width.
  const composerFooterContentKey = [
    selectedInstanceId,
    String(selectedModelForPickerWithCustomFallback),
    activeFallbackModelDisplayName ?? "",
    interactionMode,
    accessLevel.shown,
    composerFooterActionLayoutKey,
    composerFooterHasWideActions,
    isComposerPrimaryActionsCompact,
    JSON.stringify(composerModelOptions?.[selectedInstanceId] ?? null),
  ].join("|");

  // The left control group never shrinks, so when its content outgrows the
  // row it overflows (the scrollbar is hidden) and the tier steps up:
  // labels -> icons -> overflow menu. Runs in a layout effect so a tier
  // change lands before paint, and from the ResizeObserver so live resizes
  // re-evaluate without waiting for a render.
  const evaluateComposerFooterTier = useCallback(() => {
    const composerForm = composerFormRef.current;
    const leftActions = composerFooterLeftActionsRef.current;
    // A hidden footer measures as zero; keep the last tier until it shows.
    if (!composerForm || !leftActions || leftActions.clientWidth === 0) return;
    let state = composerFooterTierStateRef.current;
    if (composerFooterContentKeyRef.current !== composerFooterContentKey) {
      composerFooterContentKeyRef.current = composerFooterContentKey;
      state = resetComposerFooterTierWidths(state);
    }
    const next = resolveComposerFooterTier(state, {
      width: composerForm.clientWidth,
      overflowing: isComposerFooterOverflowing(measureComposerFooterOverflow(leftActions)),
    });
    composerFooterTierStateRef.current = next;
    setComposerFooterTier(next.tier);
  }, [composerFooterContentKey]);

  // Keeps the footer's reserve stamped on the form while the footer shows,
  // whenever its right-hand buttons change width (send/stop, dictation, a
  // plan's Implement pair). The right panel's drag limit reads it while a
  // question or an approval replaces the footer.
  const observeComposerFooterReserve = useCallback((rightActions: HTMLDivElement | null) => {
    if (!rightActions || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const footer = rightActions.parentElement;
      const composerForm = composerFormRef.current;
      if (!footer || !composerForm || rightActions.clientWidth === 0) return;
      composerForm.setAttribute(
        COMPOSER_FOOTER_RESERVE_ATTRIBUTE,
        String(Math.ceil(measureComposerFooterReserve(footer))),
      );
    });
    observer.observe(rightActions);
    return () => observer.disconnect();
  }, []);

  // Re-measure after every tier change: the narrower tier may still overflow,
  // and a roomier one may fit again.
  useLayoutEffect(() => {
    if (composerFooterTierStateRef.current.tier !== composerFooterTier) return;
    evaluateComposerFooterTier();
  }, [evaluateComposerFooterTier, composerFooterTier]);

  useLayoutEffect(() => {
    const composerForm = composerFormRef.current;
    if (!composerForm) return;
    const measurePrimaryActionsCompact = () =>
      shouldUseCompactComposerPrimaryActions(composerForm.clientWidth, {
        hasWideActions: composerFooterHasWideActions,
      });

    composerFormHeightRef.current = composerForm.getBoundingClientRect().height;
    setIsComposerPrimaryActionsCompact(measurePrimaryActionsCompact());
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;
      setIsComposerPrimaryActionsCompact(measurePrimaryActionsCompact());
      evaluateComposerFooterTier();
      const nextHeight = entry.contentRect.height;
      const previousHeight = composerFormHeightRef.current;
      composerFormHeightRef.current = nextHeight;
      if (previousHeight > 0 && Math.abs(nextHeight - previousHeight) < 0.5) return;
      if (!shouldAutoScrollRef.current) return;
      scheduleStickToBottom();
    });

    observer.observe(composerForm);
    return () => {
      observer.disconnect();
    };
  }, [
    activeThreadId,
    composerFooterActionLayoutKey,
    composerFooterHasWideActions,
    evaluateComposerFooterTier,
    scheduleStickToBottom,
    shouldAutoScrollRef,
  ]);

  // ------------------------------------------------------------------
  // Image persist effect
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (composerAttachments.length === 0) {
        clearComposerDraftPersistedAttachments(composerDraftTarget);
        return;
      }
      const getPersistedAttachmentsForThread = () =>
        getComposerDraft(composerDraftTarget)?.persistedAttachments ?? [];
      try {
        const currentPersistedAttachments = getPersistedAttachmentsForThread();
        const existingPersistedById = new Map(
          currentPersistedAttachments.map((attachment) => [attachment.id, attachment]),
        );
        const stagedAttachmentById = new Map<string, PersistedComposerAttachment>();
        await Promise.all(
          composerAttachments.map(async (image) => {
            try {
              const dataUrl = await readFileAsDataUrl(image.file);
              stagedAttachmentById.set(image.id, {
                id: image.id,
                name: image.name,
                mimeType: image.mimeType,
                sizeBytes: image.sizeBytes,
                dataUrl,
              });
            } catch {
              const existingPersisted = existingPersistedById.get(image.id);
              if (existingPersisted) {
                stagedAttachmentById.set(image.id, existingPersisted);
              }
            }
          }),
        );
        const serialized = Array.from(stagedAttachmentById.values());
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, serialized);
      } catch {
        const currentImageIds = new Set(composerAttachments.map((image) => image.id));
        const fallbackPersistedAttachments = getPersistedAttachmentsForThread();
        const fallbackPersistedIds = fallbackPersistedAttachments
          .map((attachment) => attachment.id)
          .filter((id) => currentImageIds.has(id));
        const fallbackPersistedIdSet = new Set(fallbackPersistedIds);
        const fallbackAttachments = fallbackPersistedAttachments.filter((attachment) =>
          fallbackPersistedIdSet.has(attachment.id),
        );
        if (cancelled) return;
        syncComposerDraftPersistedAttachments(composerDraftTarget, fallbackAttachments);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    composerDraftTarget,
    clearComposerDraftPersistedAttachments,
    composerAttachments,
    getComposerDraft,
    syncComposerDraftPersistedAttachments,
  ]);

  // ------------------------------------------------------------------
  // Callbacks: prompt change
  // ------------------------------------------------------------------
  const onPromptChange = useCallback(
    (
      nextPrompt: string,
      nextCursor: number,
      expandedCursor: number,
      cursorAdjacentToMention: boolean,
      terminalContextIds: string[],
    ) => {
      const previousPrompt = promptRef.current;
      promptRef.current = nextPrompt;
      setPrompt(nextPrompt);
      const shouldSyncInlineTerminalContexts =
        previousPrompt.includes(INLINE_TERMINAL_CONTEXT_PLACEHOLDER) ||
        nextPrompt.includes(INLINE_TERMINAL_CONTEXT_PLACEHOLDER) ||
        terminalContextIds.length > 0;
      if (
        shouldSyncInlineTerminalContexts &&
        !terminalContextIdListsEqual(composerTerminalContexts, terminalContextIds)
      ) {
        setComposerDraftTerminalContexts(
          composerDraftTarget,
          syncTerminalContextsByIds(composerTerminalContexts, terminalContextIds),
        );
      }
      setComposerCursor(nextCursor);
      setComposerTrigger(
        cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
      );
    },
    [
      promptRef,
      setPrompt,
      composerDraftTarget,
      composerTerminalContexts,
      setComposerDraftTerminalContexts,
    ],
  );

  // ------------------------------------------------------------------
  // Callbacks: prompt replacement / menu
  // ------------------------------------------------------------------
  const applyPromptReplacement = useCallback(
    (
      rangeStart: number,
      rangeEnd: number,
      replacement: string,
      options?: { expectedText?: string; focusEditorAfterReplace?: boolean },
    ): boolean => {
      const currentText = promptRef.current;
      const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
      const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
      if (
        options?.expectedText !== undefined &&
        currentText.slice(safeStart, safeEnd) !== options.expectedText
      ) {
        return false;
      }
      const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
      const nextCursor = collapseExpandedComposerCursor(next.text, next.cursor);
      const nextExpandedCursor = expandCollapsedComposerCursor(next.text, nextCursor);
      promptRef.current = next.text;
      setPrompt(next.text);
      setComposerCursor(nextCursor);
      setComposerTrigger(detectComposerTrigger(next.text, nextExpandedCursor));
      if (options?.focusEditorAfterReplace !== false) {
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAt(nextCursor);
        });
      }
      return true;
    },
    [promptRef, setPrompt],
  );

  // Dictation adds to what is typed instead of replacing it, so it lands at
  // the caret through the same replacement path the command menu uses, with a
  // space added on either side only where the neighbouring character needs one.
  const insertDictatedText = useCallback(
    (text: string) => {
      const currentText = promptRef.current;
      const cursor = Math.max(0, Math.min(currentText.length, composerCursor));
      const before = currentText.slice(0, cursor).slice(-1);
      const after = currentText.slice(cursor).slice(0, 1);
      const prefix = before !== "" && !/\s/.test(before) ? " " : "";
      const suffix = after !== "" && !/\s/.test(after) ? " " : "";
      applyPromptReplacement(cursor, cursor, `${prefix}${text}${suffix}`, {
        focusEditorAfterReplace: true,
      });
    },
    [applyPromptReplacement, composerCursor, promptRef],
  );

  const dictation = useDictation({ environmentId, onText: insertDictatedText });
  const dictationNotice: ComposerNotice | null = dictation.error
    ? {
        id: "dictation",
        severity: "error",
        lead: "Dictation failed.",
        detail: dictation.error,
        dismissLabel: "Dismiss",
        onDismiss: dictation.clearError,
      }
    : null;
  const modelRetirementNotice = useModelRetirementNotice({
    // A room message to another agent isn't sent with this model, and the
    // picker that could change it is hidden, so neither is the notice.
    model: addressingRoomAgent
      ? null
      : (selectedProviderModels.find((candidate) => candidate.slug === selectedModel) ?? null),
    pickable: modelOptionsByInstance.get(selectedInstanceId) ?? [],
    onSwitch: (slug) => onProviderModelSelect(selectedInstanceId, slug),
  });
  const dockedNotices = selectComposerNotices([...notices, dictationNotice, modelRetirementNotice]);

  const readComposerSnapshot = useCallback((): {
    value: string;
    cursor: number;
    expandedCursor: number;
    terminalContextIds: string[];
  } => {
    const editorSnapshot = composerEditorRef.current?.readSnapshot();
    if (editorSnapshot) {
      return editorSnapshot;
    }
    return {
      value: promptRef.current,
      cursor: composerCursor,
      expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
      terminalContextIds: composerTerminalContexts.map((context) => context.id),
    };
  }, [composerCursor, composerTerminalContexts, promptRef]);

  // Tab, or a click on its key, takes the suggestion: all of it into an empty
  // box, or the rest of it after the words typed so far.
  const acceptPromptSuggestion = useCallback((): boolean => {
    if (!promptSuggestion || promptSuggestionView === null) {
      return false;
    }
    const snapshot = readComposerSnapshot();
    const view = resolvePromptSuggestionView({
      prompt: snapshot.value,
      cursor: snapshot.cursor,
      suggestion: promptSuggestion.text,
    });
    // Only the rest that is on screen: not with words selected or the editor scrolled.
    if (
      !view ||
      (view.kind === "completion" && composerEditorRef.current?.canShowCompletion() === false)
    ) {
      return false;
    }
    const nextPrompt =
      view.kind === "placeholder" ? promptSuggestion.text : snapshot.value + view.remainder;
    promptRef.current = nextPrompt;
    setPrompt(nextPrompt);
    const nextCursor = collapseExpandedComposerCursor(nextPrompt, nextPrompt.length);
    setComposerCursor(nextCursor);
    setComposerTrigger(detectComposerTrigger(nextPrompt, nextPrompt.length));
    window.requestAnimationFrame(() => {
      composerEditorRef.current?.focusAt(nextCursor);
    });
    return true;
  }, [promptRef, promptSuggestion, promptSuggestionView, readComposerSnapshot, setPrompt]);
  // Up and Down bring back what the user wrote here before (shell style):
  // see stepPromptRecall. The recalled text is remembered so an edit hands
  // the arrows back to the caret.
  const promptRecallRef = useRef<PromptRecall | null>(null);
  const stepRecall = useCallback(
    (key: "ArrowUp" | "ArrowDown"): boolean => {
      const step = stepPromptRecall({
        key,
        value: readComposerSnapshot().value,
        recall: promptRecallRef.current,
        prompts: recallablePrompts(threadMessages ?? []),
      });
      if (step === null) {
        promptRecallRef.current = null;
        return false;
      }
      promptRecallRef.current = step.recall;
      promptRef.current = step.value;
      setPrompt(step.value);
      const nextCursor = collapseExpandedComposerCursor(step.value, step.value.length);
      setComposerCursor(nextCursor);
      setComposerTrigger(null);
      window.requestAnimationFrame(() => {
        composerEditorRef.current?.focusAt(nextCursor);
      });
      return true;
    },
    [promptRef, readComposerSnapshot, setPrompt, threadMessages],
  );
  // Stays set while the box fills and empties again, so the editor keeps the
  // suggestion mounted and its fade-in plays once.
  const promptSuggestionDisplay = useMemo<ComposerPromptSuggestionDisplay | null>(() => {
    if (
      !promptSuggestion ||
      isConnecting ||
      isComposerApprovalState ||
      hasBlockingQuestion ||
      composerUnavailable
    ) {
      return null;
    }
    return {
      id: promptSuggestion.id,
      text: promptSuggestion.text,
      remainder:
        promptSuggestionView?.kind === "completion" ? promptSuggestionView.remainder : null,
      hint: composerCapabilityHint,
      onAccept: () => {
        acceptPromptSuggestion();
      },
    };
  }, [
    acceptPromptSuggestion,
    composerCapabilityHint,
    composerUnavailable,
    hasBlockingQuestion,
    isComposerApprovalState,
    isConnecting,
    promptSuggestion,
    promptSuggestionView,
  ]);

  const resolveActiveComposerTrigger = useCallback((): {
    snapshot: { value: string; cursor: number; expandedCursor: number };
    trigger: ComposerTrigger | null;
  } => {
    const snapshot = readComposerSnapshot();
    return {
      snapshot,
      trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
    };
  }, [readComposerSnapshot]);

  const onSelectComposerItem = useCallback(
    (item: ComposerCommandItem) => {
      if (composerSelectLockRef.current) return;
      composerSelectLockRef.current = true;
      window.requestAnimationFrame(() => {
        composerSelectLockRef.current = false;
      });
      const { snapshot, trigger } = resolveActiveComposerTrigger();
      if (!trigger) return;
      if (item.type === "room-agent") {
        // The "@name" goes; the message now goes to that agent.
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          setComposerHighlightedItemId(null);
          useRoomRecipientStore.getState().choose(routeThreadRef, item.participantId);
        }
        return;
      }
      if (item.type === "path") {
        const replacement = `@${serializeComposerMentionPath(item.path)} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "slash-command") {
        if (item.command === "model") {
          const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
            expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
            focusEditorAfterReplace: false,
          });
          if (applied) {
            setComposerHighlightedItemId(null);
            setIsComposerModelPickerOpen(true);
          }
          return;
        }
        if (item.command === "goal") {
          const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
            expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
            focusEditorAfterReplace: false,
          });
          if (applied) {
            setComposerHighlightedItemId(null);
            setIsGoalEditorOpen(true);
          }
          return;
        }
        void handleInteractionModeChange(item.command === "plan" ? "plan" : "default");
        const applied = applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
          expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
        });
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "provider-slash-command") {
        const replacement = `/${item.command.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
      if (item.type === "skill") {
        const replacement = `$${item.skill.name} `;
        const replacementRangeEnd = extendReplacementRangeForTrailingSpace(
          snapshot.value,
          trigger.rangeEnd,
          replacement,
        );
        const applied = applyPromptReplacement(
          trigger.rangeStart,
          replacementRangeEnd,
          replacement,
          { expectedText: snapshot.value.slice(trigger.rangeStart, replacementRangeEnd) },
        );
        if (applied) {
          setComposerHighlightedItemId(null);
        }
        return;
      }
    },
    [
      applyPromptReplacement,
      handleInteractionModeChange,
      resolveActiveComposerTrigger,
      routeThreadRef,
    ],
  );

  const onComposerMenuItemHighlighted = useCallback(
    (itemId: string | null) => {
      setComposerHighlightedItemId(itemId);
      setComposerHighlightedSearchKey(composerMenuSearchKey);
    },
    [composerMenuSearchKey],
  );

  const nudgeComposerMenuHighlight = useCallback(
    (key: "ArrowDown" | "ArrowUp") => {
      if (composerMenuItems.length === 0) return;
      const highlightedIndex = composerMenuItems.findIndex(
        (item) => item.id === composerHighlightedItemId,
      );
      const normalizedIndex =
        highlightedIndex >= 0 ? highlightedIndex : key === "ArrowDown" ? -1 : 0;
      const offset = key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        (normalizedIndex + offset + composerMenuItems.length) % composerMenuItems.length;
      const nextItem = composerMenuItems[nextIndex];
      setComposerHighlightedItemId(nextItem?.id ?? null);
    },
    [composerHighlightedItemId, composerMenuItems],
  );

  const blurMobileComposerAfterSend = useCallback(() => {
    if (!isMobileViewport) return;
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    setIsComposerFocused(false);
  }, [isMobileViewport]);

  const shouldBlurMobileComposerOnSubmit = useCallback(() => {
    if (!isMobileViewport) return false;
    if (isSendBusy || isConnecting || phase === "running") return false;
    return showPlanFollowUpPrompt || composerSendState.hasSendableContent;
  }, [
    composerSendState.hasSendableContent,
    isConnecting,
    isMobileViewport,
    isSendBusy,
    phase,
    showPlanFollowUpPrompt,
  ]);

  const submitComposer = useCallback(
    (event?: { preventDefault: () => void }) => {
      if (hasBlockingQuestion) {
        event?.preventDefault();
        return;
      }
      if (composerAttachments.length > 0 && !selectedModelSupportsImages) {
        event?.preventDefault();
        toastManager.add({
          type: "error",
          title: "Selected model doesn't accept images",
        });
        return;
      }
      // Sending a follow-up to a completed turn consumes that turn's suggestion;
      // keep it hidden until a newer turn produces its own.
      const latestTurn = activeThread?.latestTurn ?? null;
      if (composerSendState.hasSendableContent && latestTurn?.state === "completed") {
        setDismissedSuggestionTurnId(latestTurn.turnId);
      }
      onSend(event);
      if (shouldBlurMobileComposerOnSubmit()) {
        blurMobileComposerAfterSend();
      }
    },
    [
      activeThread?.latestTurn,
      hasBlockingQuestion,
      blurMobileComposerAfterSend,
      composerAttachments.length,
      composerSendState.hasSendableContent,
      onSend,
      selectedModelSupportsImages,
      shouldBlurMobileComposerOnSubmit,
    ],
  );
  const expandMobileComposer = useCallback(() => {
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
      composerBlurFrameRef.current = null;
    }
    if (mobileComposerExpandFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
    }
    if (mobileComposerExpandReleaseFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
    }
    mobileComposerExpandInFlightRef.current = true;
    setIsComposerFocused(true);
    mobileComposerExpandFrameRef.current = window.requestAnimationFrame(() => {
      mobileComposerExpandFrameRef.current = null;
      composerEditorRef.current?.focusAtEnd();
      mobileComposerExpandReleaseFrameRef.current = window.requestAnimationFrame(() => {
        mobileComposerExpandReleaseFrameRef.current = null;
        mobileComposerExpandInFlightRef.current = false;
      });
    });
  }, []);

  // ------------------------------------------------------------------
  // Callbacks: command key
  // ------------------------------------------------------------------
  const onComposerCommandKey = (key: ComposerCommandKey, event: KeyboardEvent) => {
    if (key === "Tab" && event.shiftKey) {
      toggleInteractionMode();
      return true;
    }
    const { trigger } = resolveActiveComposerTrigger();
    const menuIsActive = composerMenuOpenRef.current || trigger !== null;
    if (menuIsActive) {
      const currentItems = composerMenuItemsRef.current;
      const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
      if (key === "ArrowDown" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowDown");
        return true;
      }
      if (key === "ArrowUp" && currentItems.length > 0) {
        nudgeComposerMenuHighlight("ArrowUp");
        return true;
      }
      if ((key === "Enter" || key === "Tab") && selectedItem) {
        onSelectComposerItem(selectedItem);
        return true;
      }
    }
    // Up and Down walk the user's earlier prompts while the box is empty or
    // still holds one unedited; an open menu above keeps the arrows.
    if (
      (key === "ArrowUp" || key === "ArrowDown") &&
      !menuIsActive &&
      !event.shiftKey &&
      !event.altKey &&
      !event.metaKey &&
      !event.ctrlKey &&
      stepRecall(key)
    ) {
      return true;
    }
    // Tab takes the agent's suggestion in the box: all of it when the box is
    // empty, or the rest when the typed words start it, so it never clobbers
    // other text. The menu branch above already claimed Tab when a slash or
    // mention menu is open.
    if (key === "Tab" && acceptPromptSuggestion()) {
      return true;
    }
    // Escape puts the usual hint back until a newer turn leaves a suggestion.
    if (
      key === "Escape" &&
      !menuIsActive &&
      promptSuggestionView !== null &&
      !(
        promptSuggestionView.kind === "completion" &&
        composerEditorRef.current?.canShowCompletion() === false
      )
    ) {
      setDismissedSuggestionTurnId(activeThread?.latestTurn?.turnId ?? null);
      return true;
    }
    if (key === "Enter" && !event.shiftKey) {
      submitComposer();
      return true;
    }
    return false;
  };

  // ------------------------------------------------------------------
  // Callbacks: images
  // ------------------------------------------------------------------
  /** Returns whether at least one file made it into the draft, so callers can
   *  confirm the attachment rather than the attempt. */
  const addComposerFiles = (files: File[]): boolean => {
    if (!activeThreadId || files.length === 0) return false;
    if (hasBlockingQuestion) {
      toastManager.add({
        type: "error",
        title: "Attach files after answering plan questions",
      });
      return false;
    }
    const nextAttachments: ComposerAttachment[] = [];
    let nextAttachmentCount = composerAttachmentsRef.current.length;
    let error: string | null = null;
    let rejectedImageForModel = false;
    for (const file of files) {
      const isImage = file.type.startsWith("image/");
      const fileType = isImage
        ? null
        : resolveFileAttachmentType({ mimeType: file.type, fileName: file.name });
      if (!isImage && !fileType) {
        error = `Unsupported file type for '${file.name}'. Attach images, PDFs, or text, Markdown, and CSV files.`;
        continue;
      }
      if (isImage && !selectedModelSupportsImages) {
        rejectedImageForModel = true;
        continue;
      }
      const maxBytes = isImage
        ? PROVIDER_SEND_TURN_MAX_IMAGE_BYTES
        : PROVIDER_SEND_TURN_MAX_FILE_BYTES;
      if (file.size > maxBytes) {
        error = `'${file.name}' exceeds the ${ATTACHMENT_SIZE_LIMIT_LABEL} attachment limit.`;
        continue;
      }
      if (nextAttachmentCount >= PROVIDER_SEND_TURN_MAX_ATTACHMENTS) {
        error = `You can attach up to ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS} files per message.`;
        break;
      }
      if (isImage) {
        nextAttachments.push({
          type: "image",
          id: randomUUID(),
          name: file.name || "image",
          mimeType: file.type,
          sizeBytes: file.size,
          previewUrl: URL.createObjectURL(file),
          file,
        });
      } else if (fileType) {
        nextAttachments.push({
          type: "file",
          kind: fileType.kind,
          id: randomUUID(),
          name: file.name || `file${fileType.extension}`,
          mimeType: fileType.mimeType,
          sizeBytes: file.size,
          file,
        });
      }
      nextAttachmentCount += 1;
    }
    if (rejectedImageForModel) {
      toastManager.add({
        type: "error",
        title: "Selected model doesn't accept images",
      });
    }
    if (nextAttachments.length === 1 && nextAttachments[0]) {
      addComposerAttachment(nextAttachments[0]);
    } else if (nextAttachments.length > 1) {
      addComposerAttachmentsToDraft(nextAttachments);
    }
    setThreadError(activeThreadId, error);
    return nextAttachments.length > 0;
  };

  const openImageFilePicker = () => {
    if (attachmentsDisabled) return;
    imageFileInputRef.current?.click();
  };

  const openAttachmentFilePicker = () => {
    if (attachmentsDisabled) return;
    attachmentFileInputRef.current?.click();
  };

  const onAttachmentFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset so selecting the same file again still fires a change event.
    event.target.value = "";
    if (files.length === 0) return;
    addComposerFiles(files);
    focusComposer();
  };

  const onCaptureScreenshot = () => {
    const captureScreenshot = window.desktopBridge?.captureScreenshot;
    if (!captureScreenshot || isCapturingScreenshot) return;
    if (attachmentsDisabled) return;

    setIsCapturingScreenshot(true);
    void captureScreenshot({ mode: "interactive" })
      .then((result) => {
        if (result.status === "cancelled") {
          return;
        }
        if (result.status === "unsupported" || result.status === "failed") {
          toastManager.add(
            result.status === "unsupported"
              ? {
                  type: "error",
                  title: "Can't take screenshots here",
                  description: result.message,
                }
              : failureNotice({
                  title: "Couldn't take the screenshot",
                  reason: result.message,
                }),
          );
          return;
        }

        const file = desktopCapturedScreenshotToFile(result.image);
        if (!file) {
          toastManager.add(
            failureNotice({
              title: "Couldn't take the screenshot",
              reason: "The captured image couldn't be read.",
            }),
          );
          return;
        }

        addComposerFiles([file]);
        focusComposer();
      })
      .catch((error: unknown) => {
        toastManager.add(
          failureNotice({
            title: "Couldn't take the screenshot",
            reason: unknownErrorMessage(error, "The desktop capture request failed."),
          }),
        );
      })
      .finally(() => {
        setIsCapturingScreenshot(false);
      });
  };

  const removeComposerAttachment = (imageId: string) => {
    removeComposerAttachmentFromDraft(imageId);
  };

  // ------------------------------------------------------------------
  // Callbacks: paste / drag
  // ------------------------------------------------------------------
  const onComposerPaste = (event: React.ClipboardEvent<HTMLElement>) => {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    const supportedFiles = files.filter(
      (file) =>
        file.type.startsWith("image/") ||
        resolveFileAttachmentType({ mimeType: file.type, fileName: file.name }) !== null,
    );
    if (supportedFiles.length === 0) return;
    event.preventDefault();
    addComposerFiles(supportedFiles);
  };

  const onComposerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDragOverComposer(true);
  };

  const onComposerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setIsDragOverComposer(true);
  };

  const onComposerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDragOverComposer(false);
    }
  };

  const onComposerDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOverComposer(false);
    const files = Array.from(event.dataTransfer.files);
    addComposerFiles(files);
    focusComposer();
  };
  const handleInterruptPrimaryAction = useCallback(() => {
    void onInterrupt();
  }, [onInterrupt]);
  const handleStopBackgroundWorkPrimaryAction = useCallback(() => {
    void onStopBackgroundWork();
  }, [onStopBackgroundWork]);
  // Stop beside approval decisions and blocking questions, which take the
  // footer's place. A background agent can ask after the turn settled, so the
  // wait needs its Stop here too.
  const sideStopButton = hasActiveTurn ? (
    <ComposerStopButton
      onInterrupt={handleInterruptPrimaryAction}
      preserveComposerFocusOnPointerDown={isMobileViewport}
    />
  ) : isWaitingOnBackgroundWork ? (
    <ComposerStopButton
      onInterrupt={handleStopBackgroundWorkPrimaryAction}
      preserveComposerFocusOnPointerDown={isMobileViewport}
      stops="background-work"
    />
  ) : null;
  // Typing while an approval waits: a message for the agent that asked
  // declines it and tells that agent what to do instead. In a room, a message
  // for another agent is asked or queued as usual and leaves the approval be.
  const approvalRedirectPlaceholder =
    isComposerApprovalState && !hasBlockingQuestion && roomDelivery === null
      ? `Type to decline and tell ${roomAgentLabels?.get(roomAgentKey(roomHolderId))?.name ?? "the agent"} what to do instead`
      : null;
  const approvalSendDisabled =
    isSendBusy ||
    isConnecting ||
    composerUnavailable ||
    (activePendingApproval !== null &&
      respondingRequestIds.includes(activePendingApproval.requestId));
  const approvalSendButton =
    !isComposerApprovalState ||
    hasBlockingQuestion ||
    !composerSendState.hasSendableContent ? null : roomDelivery !== null ? (
      <ComposerRoomSendButton
        roomDelivery={roomDelivery}
        onRoomDeliveryChange={handleRoomDeliveryChange}
        disabled={approvalSendDisabled}
        preserveComposerFocusOnPointerDown={isMobileViewport}
      />
    ) : (
      <ComposerDeclineAndSendButton
        disabled={approvalSendDisabled}
        preserveComposerFocusOnPointerDown={isMobileViewport}
      />
    );
  const handleImplementPlanInNewThreadPrimaryAction = useCallback(() => {
    void onImplementPlanInNewThread();
  }, [onImplementPlanInNewThread]);
  const scheduleComposerCollapseCheck = useCallback(() => {
    if (!isMobileViewport) {
      return;
    }
    if (mobileComposerExpandInFlightRef.current) {
      return;
    }
    if (composerBlurFrameRef.current !== null) {
      window.cancelAnimationFrame(composerBlurFrameRef.current);
    }
    composerBlurFrameRef.current = window.requestAnimationFrame(() => {
      composerBlurFrameRef.current = null;
      if (mobileComposerExpandInFlightRef.current) {
        return;
      }
      const composerSurface = composerSurfaceRef.current;
      const activeElement = document.activeElement;
      if (activeElement instanceof Element && isInsideComposerFloatingLayer(activeElement)) {
        return;
      }
      if (
        composerSurface &&
        activeElement instanceof Node &&
        composerSurface.contains(activeElement)
      ) {
        return;
      }
      setIsComposerFocused(false);
    });
  }, [isMobileViewport]);

  useEffect(() => {
    return () => {
      if (composerBlurFrameRef.current !== null) {
        window.cancelAnimationFrame(composerBlurFrameRef.current);
      }
      if (mobileComposerExpandFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandFrameRef.current);
      }
      if (mobileComposerExpandReleaseFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileComposerExpandReleaseFrameRef.current);
      }
    };
  }, []);

  // ------------------------------------------------------------------
  // Imperative handle
  // ------------------------------------------------------------------
  useImperativeHandle(
    composerRef,
    () => ({
      focusAtEnd: () => {
        composerEditorRef.current?.focusAtEnd();
      },
      focusAt: (cursor: number) => {
        composerEditorRef.current?.focusAt(cursor);
      },
      openModelPicker: () => {
        setIsComposerModelPickerOpen(true);
      },
      openGoalEditor: () => {
        setIsGoalEditorOpen(true);
      },
      toggleModelPicker: () => {
        setIsComposerModelPickerOpen((open) => !open);
      },
      isModelPickerOpen: () => isComposerModelPickerOpen,
      readSnapshot: () => {
        return readComposerSnapshot();
      },
      resetCursorState: (options?: {
        cursor?: number;
        prompt?: string;
        detectTrigger?: boolean;
      }) => {
        const promptForState = options?.prompt ?? promptRef.current;
        const cursor = clampCollapsedComposerCursor(promptForState, options?.cursor ?? 0);
        setComposerHighlightedItemId(null);
        setComposerCursor(cursor);
        setComposerTrigger(
          options?.detectTrigger
            ? detectComposerTrigger(
                promptForState,
                expandCollapsedComposerCursor(promptForState, cursor),
              )
            : null,
        );
      },
      addTerminalContext: (selection: TerminalContextSelection) => {
        if (!activeThread) return;
        addComposerDraftTerminalContext(composerDraftTarget, {
          id: randomUUID(),
          threadId: activeThread.id,
          createdAt: new Date().toISOString(),
          ...selection,
        });
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAtEnd();
        });
      },
      addTranscriptHighlightContext: (selection: TranscriptHighlightContextSelection) => {
        if (!activeThread) return;
        addComposerDraftTranscriptHighlightContext(composerDraftTarget, {
          ...selection,
          id: randomUUID(),
          threadId: activeThread.id,
          createdAt: new Date().toISOString(),
        });
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAtEnd();
        });
      },
      addPickedElementContext: (context: PickedElementContext) => {
        if (!activeThread) return;
        const existing = composerPickedElementContextsRef.current;
        // A re-pick that says nothing new -- same element, same note, same
        // grouping -- is a miss-click or a re-check, and one context is
        // enough. One that carries a new note or joins a new group is a new
        // statement about the element and gets its own chip.
        const key = pickedElementContextDedupKey(context);
        if (existing.some((entry) => pickedElementContextDedupKey(entry) === key)) {
          return;
        }
        commitPickedElementContexts([
          ...existing,
          {
            ...context,
            id: randomUUID(),
            threadId: activeThread.id,
            createdAt: new Date().toISOString(),
          },
        ]);
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAtEnd();
        });
      },
      addDrawingContext: (context: DrawingContext) => {
        if (!activeThread) return;
        commitDrawingContexts([
          ...drawingContextsRef.current,
          {
            ...context,
            id: randomUUID(),
            threadId: activeThread.id,
            createdAt: new Date().toISOString(),
          },
        ]);
        focusComposer();
      },
      addScreenshotAttachment: ({ dataUrl, name }) => {
        // The same two steps the desktop screen capture takes, for the same
        // reasons: the converter rejects anything that is not a PNG data URL,
        // and addComposerFiles applies the model-accepts-images check, the size
        // limits and the dedupe that this would otherwise have to restate.
        const file = desktopCapturedScreenshotToFile({ dataUrl, name, mimeType: "image/png" });
        if (file === null) {
          toastManager.add(
            failureNotice({
              title: "Couldn't take the screenshot",
              reason: "The captured image couldn't be read.",
            }),
          );
          return false;
        }
        const attached = addComposerFiles([file]);
        if (attached) {
          focusComposer();
        }
        return attached;
      },
      addTextFileAttachment: ({ name, text }) => {
        // Through addComposerFiles for the same reason the screenshot goes
        // there: the size limits, the attachment cap and the dedupe all live
        // in one place.
        const attached = addComposerFiles([new File([text], name, { type: "text/plain" })]);
        if (attached) {
          focusComposer();
        }
        return attached;
      },
      getSendContext: () => ({
        prompt: promptRef.current,
        images: composerAttachmentsRef.current,
        terminalContexts: composerTerminalContextsRef.current,
        transcriptHighlightContexts: composerTranscriptHighlightContextsRef.current,
        fileSelectionContexts: composerFileSelectionContextsRef.current,
        pickedElementContexts: composerPickedElementContextsRef.current,
        drawingContexts: drawingContextsRef.current,
        selectedPromptEffort,
        selectedModelOptionsForDispatch,
        selectedModelSelection,
        selectedProvider,
        selectedModel,
        selectedProviderModels,
        skillReferences: resolveComposerSkillReferences(promptRef.current, composerSkills),
      }),
    }),
    [
      activeThread,
      addComposerDraftTerminalContext,
      addComposerDraftTranscriptHighlightContext,
      composerDraftTarget,
      promptRef,
      composerAttachmentsRef,
      composerTerminalContextsRef,
      composerTranscriptHighlightContextsRef,
      composerFileSelectionContextsRef,
      isComposerModelPickerOpen,
      readComposerSnapshot,
      selectedModel,
      selectedModelOptionsForDispatch,
      selectedModelSelection,
      selectedPromptEffort,
      selectedProvider,
      selectedProviderModels,
      composerSkills,
    ],
  );

  // Render
  // ------------------------------------------------------------------
  return (
    <form
      ref={composerFormRef}
      onSubmit={submitComposer}
      className="relative mx-auto w-full min-w-0 max-w-4xl"
      data-chat-composer-form="true"
    >
      {/* Clipped sideways so a long line never widens the form, whose scroll
          width the right panel's resize check reads. */}
      <div className="min-w-0 overflow-x-clip">{stackedAbove}</div>
      {!isComposerCollapsedMobile && goalBarVisible && goalControls ? (
        <div className="mx-auto w-[calc(100%-5rem)] overflow-hidden rounded-t-3xl border border-b-0 border-border/55 bg-card/60">
          <ComposerGoalBar
            goal={threadGoal}
            controls={goalControls}
            editorOpen={isGoalEditorOpen}
            isDispatching={goalDispatching === true}
            onOpenEditor={() => setIsGoalEditorOpen(true)}
            onCloseEditor={() => setIsGoalEditorOpen(false)}
            onSetGoal={(input) => {
              onSetThreadGoal?.(input);
              setIsGoalEditorOpen(false);
            }}
            onClearGoal={() => {
              onClearThreadGoal?.();
              setIsGoalEditorOpen(false);
            }}
          />
        </div>
      ) : null}
      <div
        className={cn(
          "group rounded-[13px] p-px transition-[background-color,box-shadow] duration-200",
          interactionMode === "plan" && "plan-mode-frame",
          composerProviderState.composerFrameClassName,
        )}
        onDragEnter={onComposerDragEnter}
        onDragOver={onComposerDragOver}
        onDragLeave={onComposerDragLeave}
        onDrop={onComposerDrop}
      >
        {/* A collapsed composer drops the standing context, never a notice
            about sending: those stay in front of the send button. */}
        <ComposerDock
          pullRequests={isComposerCollapsedMobile ? NO_COMPOSER_PULL_REQUESTS : pullRequests}
          notices={dockedNotices}
        />
        <div
          ref={composerSurfaceRef}
          data-chat-composer-mobile-collapsed={isComposerCollapsedMobile ? "true" : "false"}
          className={cn(
            // The one raised box on the canvas: card fill and a border. In light
            // mode a white box on the near-white canvas also needs the soft drop
            // to read; in dark the lighter fill does that on its own.
            "rounded-4xl border bg-card not-dark:elevate-raised transition-colors duration-200 has-focus-visible:border-focus-ring/45",
            !isComposerCollapsedMobile &&
              hasComposerDockContent({ pullRequests, notices: dockedNotices }) &&
              "rounded-t-none",
            isDragOverComposer ? "border-primary/70 bg-accent/30" : "border-border",
            composerUnavailable ? "opacity-75" : null,
            composerProviderState.composerSurfaceClassName,
          )}
          onFocusCapture={(event) => {
            const activeElement = event.target;
            if (
              isComposerCollapsedMobile &&
              activeElement instanceof HTMLElement &&
              activeElement.closest('[data-chat-composer-collapsed-controls="true"]')
            ) {
              return;
            }
            if (composerBlurFrameRef.current !== null) {
              window.cancelAnimationFrame(composerBlurFrameRef.current);
              composerBlurFrameRef.current = null;
            }
            setIsComposerFocused(true);
          }}
          onBlurCapture={() => {
            scheduleComposerCollapseCheck();
          }}
        >
          {!isComposerCollapsedMobile &&
            (activePendingApproval ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerPendingApprovalPanel
                  approval={activePendingApproval}
                  pendingCount={pendingApprovals.length}
                />
              </div>
            ) : pendingUserInputs.length > 0 ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerPendingUserInputPanel
                  pendingUserInputs={pendingUserInputs}
                  respondingRequestIds={respondingUserInputRequestIds}
                  answers={activePendingDraftAnswers}
                  questionIndex={activePendingQuestionIndex}
                  isTimelineScrolledAway={isTimelineScrolledAway}
                  onToggleOption={onSelectActivePendingUserInputOption}
                  onAdvance={onAdvanceActivePendingUserInput}
                  {...(onRespondToUserInput ? { onRespond: onRespondToUserInput } : {})}
                  onPrevious={onPreviousActivePendingUserInputQuestion}
                  onCustomAnswerChange={onChangeActivePendingUserInputCustomAnswer}
                  isUnavailable={composerUnavailable}
                  isAgentRunning={hasActiveTurn}
                />
              </div>
            ) : pendingInvite !== null ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerAgentInvitePanel
                  key={pendingInvite.requestId}
                  threadRef={routeThreadRef}
                  invite={pendingInvite}
                />
              </div>
            ) : pendingChildThreadStarts !== null ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerChildThreadsPanel
                  key={pendingChildThreadStarts.batchId}
                  threadRef={routeThreadRef}
                  request={pendingChildThreadStarts}
                />
              </div>
            ) : pendingScheduleRequest !== undefined ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerScheduleRequestPanel
                  key={pendingScheduleRequest.requestId}
                  threadRef={routeThreadRef}
                  request={pendingScheduleRequest}
                />
              </div>
            ) : pendingRemoteStart !== null ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerRemoteStartPanel
                  key={`${pendingRemoteStart.environmentId}:${pendingRemoteStart.request.requestId}`}
                  pending={pendingRemoteStart}
                />
              </div>
            ) : showPlanFollowUpPrompt && activeProposedPlan ? (
              <div className="rounded-t-[11px] border-b border-border/65 bg-muted/20">
                <ComposerPlanFollowUpBanner
                  key={activeProposedPlan.id}
                  planTitle={proposedPlanTitle(activeProposedPlan.planMarkdown) ?? null}
                />
              </div>
            ) : null)}

          {isComposerCollapsedMobile && activePendingApproval ? (
            <div
              className="rounded-t-[11px] border-b border-border/65 bg-muted/20"
              data-chat-composer-collapsed-controls="true"
            >
              <ComposerPendingApprovalPanel
                approval={activePendingApproval}
                pendingCount={pendingApprovals.length}
              />
              <div className="flex flex-wrap items-center justify-end gap-2 px-3 pb-3 sm:px-4">
                <ComposerPendingApprovalActions
                  // A new request gets new buttons: focus left on one answer
                  // must not let Enter give it to the next request.
                  key={activePendingApproval.requestId}
                  requestId={activePendingApproval.requestId}
                  availableDecisions={activePendingApproval.availableDecisions}
                  decisionWarnings={activePendingApproval.decisionWarnings}
                  defaultToNo={activePendingApproval.defaultToNo}
                  isResponding={respondingRequestIds.includes(activePendingApproval.requestId)}
                  onRespondToApproval={onRespondToApproval}
                />
                {sideStopButton}
              </div>
            </div>
          ) : null}

          {showCollapsedMobilePromptRow ? (
            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <button
                type="button"
                className={cn(
                  "min-w-0 flex-1 truncate bg-transparent p-0 text-left text-[14px] focus:outline-none",
                  prompt.trim() ? "text-foreground" : "text-muted-foreground/35",
                )}
                onPointerDown={(event) => event.preventDefault()}
                onClick={expandMobileComposer}
                aria-label="Expand composer"
              >
                {prompt.trim() || "Ask anything..."}
              </button>
              <button
                type="button"
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-30"
                disabled={collapsedComposerPrimaryActionDisabled}
                aria-label={collapsedComposerPrimaryActionLabel}
                onPointerDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  event.stopPropagation();
                  submitComposer();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M8 3L8 13M8 3L4 7M8 3L12 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ) : null}

          {isComposerBlockedByQuestion ? (
            <div
              data-chat-composer-blocked-by-question="true"
              className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4"
            >
              <p
                className={cn(
                  "min-w-0 flex-1 truncate text-[14px] leading-6",
                  prompt.trim() ? "text-muted-foreground/70" : "text-muted-foreground/40",
                )}
              >
                {prompt.trim() || "Answer the question above, or stop the turn"}
              </p>
              {sideStopButton}
            </div>
          ) : (
            <>
              <div
                ref={composerMenuAnchorRef}
                className={cn(
                  "relative px-3 pb-2 sm:px-4",
                  hasComposerHeader ? "pt-2.5 sm:pt-3" : "pt-3.5 sm:pt-4",
                  isComposerCollapsedMobile && "hidden",
                )}
              >
                {composerMenuOpen && (
                  <div className="absolute inset-x-0 bottom-full z-20 mb-2 px-1">
                    <ComposerCommandMenu
                      maxListHeightPx={composerMenuRoom}
                      items={composerMenuItems}
                      resolvedTheme={resolvedTheme}
                      isLoading={isComposerMenuLoading}
                      triggerKind={composerTriggerKind}
                      providerGroupLabel={selectedProviderDisplayName}
                      emptyStateText={composerMenuEmptyState}
                      activeItemId={activeComposerMenuItem?.id ?? null}
                      onHighlightedItemChange={onComposerMenuItemHighlighted}
                      onSelect={onSelectComposerItem}
                    />
                  </div>
                )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerAttachments.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {composerAttachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="relative h-16 w-16 overflow-hidden rounded-lg border border-border/80 bg-background"
                          title={attachment.name}
                        >
                          {attachment.previewUrl ? (
                            <ComposerImageAttachmentButton
                              name={attachment.name}
                              previewUrl={attachment.previewUrl}
                              onExpand={() => {
                                const preview = buildExpandedImagePreview(
                                  composerAttachments,
                                  attachment.id,
                                );
                                if (!preview) return;
                                onExpandImage(preview);
                              }}
                            />
                          ) : (
                            <button
                              type="button"
                              className="flex h-full w-full cursor-zoom-in flex-col items-center justify-center gap-1 px-1 text-center"
                              aria-label={`Preview ${attachment.name}`}
                              onClick={() => {
                                if (attachment.type !== "file") return;
                                onPreviewFile({
                                  name: attachment.name,
                                  kind: attachment.kind,
                                  // Slice re-labels the blob with the resolved canonical
                                  // MIME type (browsers report none for e.g. .md files).
                                  loadBlob: () =>
                                    Promise.resolve(
                                      attachment.file.slice(
                                        0,
                                        attachment.file.size,
                                        attachment.mimeType,
                                      ),
                                    ),
                                });
                              }}
                            >
                              <FileTextIcon className="size-5 text-muted-foreground" />
                              <span className="w-full truncate text-[9px] leading-tight text-muted-foreground/80">
                                {attachment.name}
                              </span>
                            </button>
                          )}
                          {nonPersistedComposerImageIdSet.has(attachment.id) && (
                            <Tooltip>
                              <TooltipTrigger
                                render={
                                  <span
                                    role="img"
                                    aria-label="Draft attachment may not persist"
                                    className="absolute left-1 top-1 inline-flex items-center justify-center rounded bg-background/85 p-0.5 text-amber-600"
                                  >
                                    <CircleAlertIcon className="size-3" />
                                  </span>
                                }
                              />
                              <TooltipPopup
                                side="top"
                                className="max-w-64 whitespace-normal leading-tight"
                              >
                                Draft attachment could not be saved locally and may be lost on
                                navigation.
                              </TooltipPopup>
                            </Tooltip>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-xs"
                            className="absolute right-1 top-1 bg-background/80 hover:bg-background/90"
                            onClick={() => removeComposerAttachment(attachment.id)}
                            aria-label={`Remove ${attachment.name}`}
                          >
                            <XIcon />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerTerminalContexts.length > 0 && (
                    <ComposerPendingTerminalContexts
                      contexts={composerTerminalContexts}
                      onRemove={removeComposerTerminalContextFromDraft}
                      className="mb-2"
                    />
                  )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerPickedElementContexts.length > 0 && (
                    <ComposerPendingPickedElementContexts
                      contexts={composerPickedElementContexts}
                      onRemove={removePickedElementContext}
                      onUpdateNote={updatePickedElementNote}
                      onReveal={onRevealPickedElement}
                      className="mb-2"
                    />
                  )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerDrawingContexts.length > 0 && (
                    <ComposerPendingDrawingContexts
                      contexts={composerDrawingContexts}
                      onRemove={removeDrawingContext}
                      onUpdateNote={updateDrawingNote}
                      className="mb-2"
                    />
                  )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerTranscriptHighlightContexts.length > 0 && (
                    <ComposerPendingTranscriptHighlightContexts
                      contexts={composerTranscriptHighlightContexts}
                      onRemove={removeComposerTranscriptHighlightContextFromDraft}
                      onUpdateNote={updateComposerTranscriptHighlightContextNote}
                      className="mb-2"
                    />
                  )}

                {!isComposerCollapsedMobile &&
                  !hasBlockingQuestion &&
                  composerFileSelectionContexts.length > 0 && (
                    <ComposerPendingFileSelectionContexts
                      contexts={composerFileSelectionContexts}
                      onRemove={removeComposerFileSelectionContextFromDraft}
                      className="mb-2"
                    />
                  )}

                <div className="relative">
                  <ComposerPromptEditor
                    editorRef={composerEditorRef}
                    value={prompt}
                    cursor={composerCursor}
                    terminalContexts={[]}
                    skills={composerSkills}
                    recognizedSlashCommands={recognizedSlashCommands}
                    highlightWorkflowKeyword={workflowKeywordActive && !workflowKeywordSuppressed}
                    skillAvailability={composerSkillAvailability}
                    onRemoveTerminalContext={removeComposerTerminalContextFromDraft}
                    onChange={onPromptChange}
                    onCommandKeyDown={onComposerCommandKey}
                    onPaste={onComposerPaste}
                    suggestion={promptSuggestionDisplay}
                    placeholder={
                      threadLocked
                        ? threadLocked.reason
                        : isComposerApprovalState && hasBlockingQuestion
                          ? "Resolve this approval request to continue"
                          : approvalRedirectPlaceholder !== null
                            ? approvalRedirectPlaceholder
                            : hasBlockingQuestion
                              ? "Answer the question above, or stop the turn"
                              : showPlanFollowUpPrompt && activeProposedPlan
                                ? "Add feedback to refine the plan, or leave this blank to implement it"
                                : environmentUnavailable
                                  ? `${environmentUnavailable.label} is ${
                                      environmentUnavailable.connectionState === "connecting"
                                        ? "connecting"
                                        : "disconnected"
                                    }`
                                  : defaultComposerPlaceholder
                    }
                    disabled={isConnecting || hasBlockingQuestion || composerUnavailable}
                  />
                </div>
                {workflowKeywordActive ? (
                  <ComposerWorkflowKeywordHint
                    suppressed={workflowKeywordSuppressed === true}
                    onToggle={onToggleWorkflowKeyword}
                  />
                ) : null}
              </div>

              {/* Bottom toolbar */}
              {isComposerCollapsedMobile ? null : activePendingApproval ? (
                <div className="flex flex-wrap items-center justify-end gap-2 px-2.5 pb-2.5 sm:px-3 sm:pb-3">
                  <ComposerPendingApprovalActions
                    // A new request gets new buttons: focus left on one answer
                    // must not let Enter give it to the next request.
                    key={activePendingApproval.requestId}
                    requestId={activePendingApproval.requestId}
                    availableDecisions={activePendingApproval.availableDecisions}
                    decisionWarnings={activePendingApproval.decisionWarnings}
                    defaultToNo={activePendingApproval.defaultToNo}
                    isResponding={respondingRequestIds.includes(activePendingApproval.requestId)}
                    onRespondToApproval={onRespondToApproval}
                  />
                  {approvalSendButton ?? sideStopButton}
                </div>
              ) : (
                <div
                  data-chat-composer-footer="true"
                  data-chat-composer-footer-compact={isComposerFooterCompact ? "true" : "false"}
                  data-chat-composer-footer-icon-only={isComposerFooterIconOnly ? "true" : "false"}
                  className={cn(
                    "flex min-w-0 flex-nowrap items-center justify-between overflow-visible px-2.5 pb-2.5 sm:px-3 sm:pb-3",
                    // The left group's negative margin would touch the right group
                    // once the narrower tiers fill the row; keep a real gap there.
                    composerFooterTier === "full"
                      ? "gap-x-2 gap-y-1.5 sm:gap-x-0"
                      : "gap-x-1.5 gap-y-1.5",
                  )}
                >
                  <div
                    ref={composerFooterLeftActionsRef}
                    data-chat-composer-actions="left"
                    data-model-fallback-chip={
                      activeModelFallback && activeFallbackModelDisplayName ? "true" : undefined
                    }
                    className={cn(
                      "group/composer-left -m-1 flex flex-1 items-center gap-1 overflow-x-auto p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                      "min-w-0",
                      // In a room the model button drops its faded agent icons
                      // when this row, or the fallback chip in it, leaves too
                      // little room for the name. Only rooms need the width
                      // container, so other threads keep their layout as is.
                      roomsShown && roomAgents.inRoom && "@container/composer-left",
                    )}
                  >
                    <ProviderModelPicker
                      compact={isComposerFooterCompact}
                      // In a room the button names who the message goes to,
                      // and the card lists the agents under the models.
                      room={roomsShown ? roomAgents : null}
                      activeInstanceId={selectedInstanceId}
                      model={selectedModelForPickerWithCustomFallback}
                      lockedProvider={lockedProvider}
                      instanceEntries={providerInstanceEntries}
                      keybindings={keybindings}
                      modelOptionsByInstance={modelOptionsByInstance}
                      terminalOpen={terminalOpen}
                      providerSettingsShown={isAppBackendEnvironment(environmentId)}
                      side="top"
                      open={isComposerModelPickerOpen}
                      {...(composerProviderState.modelPickerIconClassName
                        ? {
                            activeProviderIconClassName:
                              composerProviderState.modelPickerIconClassName,
                          }
                        : {})}
                      onOpenChange={(open) => {
                        setIsComposerModelPickerOpen(open);
                      }}
                      onInstanceModelChange={onProviderModelSelect}
                    />
                    {activeModelFallback && activeFallbackModelDisplayName ? (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span
                              data-chat-model-fallback-chip="true"
                              className={cn(
                                "inline-flex h-7 shrink-0 cursor-help items-center gap-1.5 rounded-full border border-warning/25 bg-warning/8 px-2 text-[11px] font-medium text-warning-foreground sm:h-6",
                                isComposerFooterIconOnly ? "max-w-36" : "max-w-52",
                              )}
                            />
                          }
                        >
                          <CircleAlertIcon aria-hidden="true" className="size-3 shrink-0" />
                          <span className="min-w-0 truncate">
                            {isComposerFooterIconOnly
                              ? activeFallbackModelDisplayName
                              : `Fallback: ${activeFallbackModelDisplayName}`}
                          </span>
                        </TooltipTrigger>
                        <TooltipPopup
                          side="top"
                          className="max-w-72 whitespace-normal leading-tight"
                        >
                          {activeModelFallback.detail ??
                            `Using ${activeModelFallback.activeModel} instead of ${activeModelFallback.requestedModel}.`}
                        </TooltipPopup>
                      </Tooltip>
                    ) : null}

                    {isComposerFooterCompact ? (
                      <CompactComposerControlsMenu
                        interactionMode={interactionMode}
                        runtimeMode={accessLevel.shown}
                        runtimeModeOptions={accessLevel.options}
                        showInteractionModeToggle={
                          composerProviderControls.showInteractionModeToggle
                        }
                        traitsMenuContent={
                          addressingRoomAgent ? (
                            showRoomAgentTraits && roomAgentTraitsProps ? (
                              <TraitsMenuContent {...roomAgentTraitsProps} />
                            ) : null
                          ) : (
                            providerTraitsMenuContent
                          )
                        }
                        onInteractionModeChange={handleInteractionModeChange}
                        onRuntimeModeChange={handleRuntimeModeChange}
                      />
                    ) : (
                      <>
                        {showRoomAgentTraits && roomAgentTraitsProps ? (
                          <>
                            <Separator
                              orientation="vertical"
                              className="mx-0.5 hidden h-4 sm:block"
                            />
                            <TraitsPicker
                              {...roomAgentTraitsProps}
                              iconOnly={composerFooterTier !== "full"}
                            />
                          </>
                        ) : null}
                        {providerTraitsPicker && !addressingRoomAgent ? (
                          <>
                            <Separator
                              orientation="vertical"
                              className="mx-0.5 hidden h-4 sm:block"
                            />
                            {providerTraitsPicker}
                          </>
                        ) : null}
                        <ComposerFooterModeControls
                          iconOnly={isComposerFooterIconOnly}
                          showInteractionModeToggle={
                            composerProviderControls.showInteractionModeToggle
                          }
                          interactionMode={interactionMode}
                          runtimeMode={accessLevel.shown}
                          runtimeModeOptions={accessLevel.options}
                          onToggleInteractionMode={toggleInteractionMode}
                          onRuntimeModeChange={handleRuntimeModeChange}
                        />
                      </>
                    )}
                  </div>

                  {/* Right side: stash + add attachments + send / stop button */}
                  <div
                    ref={observeComposerFooterReserve}
                    data-chat-composer-actions="right"
                    data-chat-composer-primary-actions-compact={
                      isComposerPrimaryActionsCompact ? "true" : "false"
                    }
                    className={cn(
                      "ml-auto flex shrink-0 flex-nowrap items-center justify-end",
                      // Compact rows spend every pixel on the model name; tighter
                      // gaps keep the always-visible stash bookmark from squeezing
                      // the model picker or the overflow menu out of the row.
                      isComposerFooterCompact ? "gap-1" : "gap-2",
                    )}
                  >
                    <input
                      ref={imageFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={onAttachmentFileInputChange}
                    />
                    <input
                      ref={attachmentFileInputRef}
                      type="file"
                      accept={ALL_ATTACHMENT_ACCEPT}
                      multiple
                      className="sr-only"
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={onAttachmentFileInputChange}
                    />
                    <ComposerStashControl {...stashControlProps} />
                    <ComposerAttachmentMenu
                      canCaptureScreenshot={canCaptureScreenshot}
                      isCapturingScreenshot={isCapturingScreenshot}
                      isMobileViewport={isMobileViewport}
                      disabled={attachmentsDisabled}
                      disabledReason={attachmentsDisabledReason}
                      onAddImage={openImageFilePicker}
                      onAttachFiles={openAttachmentFilePicker}
                      onCaptureScreenshot={onCaptureScreenshot}
                    />
                    <ComposerDictationControl
                      environmentId={environmentId}
                      disabled={dictationDisabledReason !== null}
                      disabledReason={dictationDisabledReason}
                      isMobileViewport={isMobileViewport}
                      dictation={dictation}
                    />

                    <ComposerFooterPrimaryActions
                      compact={isComposerPrimaryActionsCompact}
                      activeContextWindow={activeContextWindow}
                      providerAccountUsage={meterProviderAccountUsage}
                      providerUsageInstanceId={
                        // Sign-in runs on this computer. A thread that lives on
                        // another one gets the notice without the offer.
                        environmentId === primaryEnvironmentId ? meterInstanceId : null
                      }
                      contextWindowLabel={meterContextWindowLabel}
                      isRunning={hasActiveTurn}
                      showPlanFollowUpPrompt={
                        pendingUserInputs.length === 0 && showPlanFollowUpPrompt
                      }
                      promptHasText={prompt.trim().length > 0}
                      isSendBusy={isSendBusy}
                      isConnecting={isConnecting}
                      isEnvironmentUnavailable={composerUnavailable}
                      isPreparingWorktree={isPreparingWorktree}
                      hasSendableContent={
                        !hasBlockingQuestion && composerSendState.hasSendableContent
                      }
                      preserveComposerFocusOnPointerDown={isMobileViewport}
                      runtimeMode={accessLevel.shown}
                      runtimeModeOptions={accessLevel.options}
                      onRuntimeModeChange={handleRuntimeModeChange}
                      followUpDelivery={followUpDelivery}
                      onFollowUpDeliveryChange={handleFollowUpDeliveryChange}
                      roomDelivery={roomDelivery}
                      onRoomDeliveryChange={handleRoomDeliveryChange}
                      onStopBackgroundWork={
                        isWaitingOnBackgroundWork
                          ? handleStopBackgroundWorkPrimaryAction
                          : undefined
                      }
                      onInterrupt={handleInterruptPrimaryAction}
                      onImplementPlanInNewThread={handleImplementPlanInNewThreadPrimaryAction}
                      onResetAccountUsage={
                        canResetMeterProviderUsage ? requestMeterProviderUsageReset : undefined
                      }
                      accountUsageResetInFlight={isConsumingRateLimitResetCredit}
                      onCompactContext={meterShowsSlotHolder ? onCompactContext : undefined}
                      contextCompactDisabled={
                        meterShowsSlotHolder ? contextCompactDisabled : undefined
                      }
                      contextCompactInFlight={meterShowsSlotHolder ? contextCompactInFlight : false}
                      contextCompactDisabledReason={
                        meterShowsSlotHolder ? contextCompactDisabledReason : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {rateLimitResetCreditDialog}
    </form>
  );
});
